/**
 * Workflow Builder Page
 *
 * AI-native visual workflow designer for crypto exchange compliance.
 * Enables customers to design onboarding AND post-onboarding flows
 * via drag-and-drop or natural language AI commands.
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../../styles/tokens.css';
import dagre from 'dagre';
import {
  Plus,
  Play,
  Eye,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Undo2,
  Redo2,
  Search,
  X,
  Send,
  Loader2,
  Check,
  AlertTriangle,
  FileText,
  User,
  Camera,
  Shield,
  MapPin,
  GitBranch,
  Clock,
  Layers,
  MessageSquare,
  LayoutGrid,
  Activity,
  Wallet,
  Copy,
  Link2,
  ExternalLink,
  Cloud,
  CheckCircle,
  XCircle,
  Upload,
  Bot,
  Sparkles,
} from 'lucide-react';
import {
  useWorkflows,
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  useDeleteWorkflow,
  useUnpublishWorkflow,
  usePublishWorkflow,
  usePreviewWorkflow,
  useCreateLiveLink,
  useValidateWorkflow,
  useComponents,
  useTemplates,
} from '../../hooks/useWorkflowBuilder';
import { useToast } from '../../hooks/useToast';
import { WorkflowChat } from './WorkflowChat';
import ExecutionMonitor from './ExecutionMonitor';
import CddQuestionnaireConfig from './CddQuestionnaireConfig';
import SanctionsNodeConfig from './SanctionsNodeConfig';
import VerificationNodeConfig from './VerificationNodeConfig';
import { QuickActionsBar, useWorkflowKeyboardShortcuts } from './QuickActionsBar';
// ValidationOverlay exports available: validateWorkflow, ValidationPanel, ValidationBadge

// ============================================
// FRAMER MOTION ANIMATION VARIANTS
// ============================================

/**
 * Animation variants for workflow nodes
 * Provides hover, selection, and drag feedback
 */
const nodeAnimationVariants = {
  initial: {
    scale: 1,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  hover: {
    scale: 1.02,
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
    transition: { duration: 0.15, ease: 'easeOut' }
  },
  selected: {
    scale: 1,
    transition: { duration: 0.2, ease: 'easeOut' }
  },
  drag: {
    scale: 1.05,
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
    cursor: 'grabbing',
    transition: { duration: 0.1 }
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 }
  }
};

/**
 * Animation variants for side panels (config panel, execution panel)
 */
const panelSlideVariants = {
  hidden: {
    x: '100%',
    opacity: 0,
    transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.25, ease: [0, 0, 0.2, 1] }
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: { duration: 0.2, ease: [0.4, 0, 1, 1] }
  }
};

/**
 * Animation variants for modals
 */
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 }
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: [0, 0, 0.2, 1] }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 }
  }
};

/**
 * Animation variants for the modal backdrop
 */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } }
};

/**
 * Animation variants for component library items
 * Available for future use in component library enhancements
 */
// eslint-disable-next-line no-unused-vars
const componentItemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  hover: {
    x: 4,
    backgroundColor: 'var(--bg-hover)',
    transition: { duration: 0.15 }
  }
};

/**
 * Animation variants for staggered list items
 * Available for workflow list and template list animations
 */
// eslint-disable-next-line no-unused-vars
const listContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

// eslint-disable-next-line no-unused-vars
const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' }
  }
};

// ============================================
// AUTO-LAYOUT HELPER (using dagre)
// ============================================

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;

/**
 * Auto-layout nodes using dagre graph layout algorithm.
 * Arranges nodes in a clean vertical flow with proper spacing.
 *
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @param {string} direction - Layout direction: 'TB' (top-bottom) or 'LR' (left-right)
 * @returns {Array} - Nodes with updated positions
 */
const autoLayoutNodes = (nodes, edges, direction = 'TB') => {
  if (!nodes || nodes.length === 0) return nodes;

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 32,    // Horizontal spacing between nodes
    ranksep: 38,    // Vertical spacing between ranks
    marginx: 20,
    marginy: 20,
  });

  // Add nodes to dagre graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run the layout algorithm
  dagre.layout(dagreGraph);

  // Update node positions from dagre
  return nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (!nodeWithPosition) return node;

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });
};

// ============================================
// CUSTOM NODE TYPES
// ============================================

const nodeStyles = {
  base: {
    padding: '12px 16px',
    borderRadius: '8px',
    minWidth: '160px',
    maxWidth: '200px',
    fontSize: '13px',
    fontWeight: 500,
    border: '2px solid',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
  },
  start: {
    borderColor: '#10b981',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
  },
  end: {
    borderColor: 'var(--accent-primary)',
    background: 'linear-gradient(135deg, var(--accent-glow-medium), var(--accent-glow-subtle))',
  },
  step: {
    borderColor: 'var(--border-color)',
  },
  condition: {
    borderColor: '#f59e0b',
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
    transform: 'rotate(0deg)',
  },
  parallel: {
    borderColor: '#3b82f6',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
  },
  screening: {
    borderColor: '#3b82f6',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
  },
  escalation: {
    borderColor: '#f97316',
    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(249, 115, 22, 0.05))',
  },
  reporting: {
    borderColor: '#ef4444',
    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
  },
  monitoring: {
    borderColor: 'var(--accent-secondary)',
    background: 'linear-gradient(135deg, var(--accent-glow-medium), var(--accent-glow-subtle))',
  },
  integration: {
    borderColor: '#10b981',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
  },
  // Review Step - like Sumsub, explicit manual review routing
  review: {
    borderColor: '#a855f7',
    background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(168, 85, 247, 0.05))',
  },
  // AI Agent steps - teal, visually distinct from deterministic system steps.
  agent: {
    borderColor: '#14b8a6',
    background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15), rgba(20, 184, 166, 0.05))',
  },
};

const componentIcons = {
  // Data Collection
  'consent': Shield,
  'email': Send,
  'personal-info': User,
  'edd-questionnaire': FileText,
  // Identity Verification
  'id-document': FileText,
  'selfie': Camera,
  'liveness': User,
  'face-match': User,
  // Compliance
  'sanctions': Shield,
  'pep': Shield,
  'adverse-media': Shield,
  // Screening (granular)
  'screen-sanctions-ofac': Shield,
  'screen-sanctions-un': Shield,
  'screen-sanctions-eu': Shield,
  'screen-pep': Shield,
  'screen-adverse-media': Shield,
  // Integration
  'external-api-check': Activity,
  // Monitoring
  'enable-monitoring': Eye,
  // Reporting
  'generate-sar-draft': FileText,
  // Escalation
  'escalate-compliance': AlertTriangle,
  'escalate-committee': AlertTriangle,
  // Crypto Compliance
  'wallet-address': Wallet,
  'wallet-screening': Shield,
  // Address
  'address': MapPin,
  // Logic
  'condition': GitBranch,
  'parallel': Layers,
  'merge': GitBranch, // rotated in render
  'manual-review': MessageSquare,
  'delay': Clock,
  // Review Step (Sumsub-style manual review)
  'review-step': Eye,
};

const handleStyle = {
  width: '12px',
  height: '12px',
  background: 'var(--accent-primary)',
  border: '2px solid var(--bg-secondary)',
};

function StartNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.start,
        boxShadow: selected ? '0 0 0 2px #10b981' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="start"
    >
      <Play size={16} style={{ color: '#10b981' }} />
      <span>{data.label || 'Start'}</span>
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function EndNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.end,
        boxShadow: selected ? '0 0 0 2px var(--accent-primary)' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="end"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <Check size={16} style={{ color: 'var(--accent-primary)' }} />
      <span>{data.label || 'End'}</span>
    </motion.div>
  );
}

function StepNode({ data, selected, id }) {
  // AI agent steps (componentType starts with "agent_") are judgment steps, not
  // deterministic system steps — render them in teal with a Bot icon so the canvas
  // visually bifurcates "the agent does this" from "the system does this".
  const isAgent = String(data.componentType || data.componentId || '').startsWith('agent_');
  const Icon = isAgent ? Bot : (componentIcons[data.componentType] || FileText);
  const accent = isAgent ? '#14b8a6' : 'var(--accent-primary)';
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...(isAgent ? nodeStyles.agent : nodeStyles.step),
        boxShadow: selected ? `0 0 0 2px ${accent}` : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="step"
      data-component={data.componentType}
      data-agent={isAgent ? 'true' : undefined}
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Icon size={16} style={{ color: accent }} />
        <span>{data.label}</span>
        {isAgent && (
          <span
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '9px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#14b8a6',
              background: 'rgba(20, 184, 166, 0.12)',
              borderRadius: '4px',
              padding: '1px 5px',
            }}
          >
            <Sparkles size={9} /> AI
          </span>
        )}
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function ConditionNode({ data, selected, id }) {
  // Support multi-route conditions (routes array) or legacy yes/no
  const routes = data.routes || [
    { id: 'yes', name: 'Yes', condition: data.condition },
    { id: 'no', name: 'No (else)', isElse: true },
  ];

  // Calculate handle positions based on number of routes
  const getHandlePosition = (index, total) => {
    if (total <= 2) return index === 0 ? '30%' : '70%';
    const spacing = 100 / (total + 1);
    return `${spacing * (index + 1)}%`;
  };

  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.condition,
        maxWidth: routes.length > 2 ? '280px' : '200px',
        boxShadow: selected ? '0 0 0 2px #f59e0b' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="condition"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <GitBranch size={16} style={{ color: '#f59e0b' }} />
        <span>{data.label || 'Condition'}</span>
      </div>
      {/* Show route labels */}
      <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: '10px', color: 'var(--text-muted)', marginTop: '8px' }}>
        {routes.map((route, index) => (
          <span key={route.id} style={{ textAlign: 'center', maxWidth: `${100 / routes.length}%` }}>
            {route.name || (route.isElse ? 'Else' : `Route ${index + 1}`)}
          </span>
        ))}
      </div>
      {/* Dynamic handles for each route */}
      {routes.map((route, index) => (
        <Handle
          key={route.id}
          type="source"
          position={Position.Bottom}
          id={route.id}
          style={{ ...handleStyle, left: getHandlePosition(index, routes.length) }}
        />
      ))}
    </motion.div>
  );
}

function ParallelNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.parallel,
        boxShadow: selected ? '0 0 0 2px #3b82f6' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="parallel"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Layers size={16} style={{ color: '#3b82f6' }} />
        <span>{data.label || 'Parallel'}</span>
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
        {data.branches?.length || 0} parallel branches
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function MergeNode({ data, selected, id }) {
  // Merge mode: 'all' (wait for all branches) or 'any' (continue on first complete)
  const mode = data.mode || 'all';

  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        borderColor: '#8b5cf6',
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(139, 92, 246, 0.05))',
        boxShadow: selected ? '0 0 0 2px #8b5cf6' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="merge"
    >
      {/* Multiple target handles for incoming branches */}
      <Handle type="target" position={Position.Top} id="branch1" style={{ ...handleStyle, left: '30%' }} />
      <Handle type="target" position={Position.Top} id="branch2" style={{ ...handleStyle, left: '70%' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <GitBranch size={16} style={{ color: '#8b5cf6', transform: 'rotate(180deg)' }} />
        <span>{data.label || 'Merge'}</span>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
        {mode === 'all' ? 'Wait for all branches' : 'Continue on first'}
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function ScreeningNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.screening,
        boxShadow: selected ? '0 0 0 2px #3b82f6' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="screening"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Shield size={16} style={{ color: '#3b82f6' }} />
        <span>{data.label || 'Screening'}</span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function EscalationNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.escalation,
        boxShadow: selected ? '0 0 0 2px #f97316' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="escalation"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <AlertTriangle size={16} style={{ color: '#f97316' }} />
        <span>{data.label || 'Escalate'}</span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function ReportingNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.reporting,
        boxShadow: selected ? '0 0 0 2px #ef4444' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="reporting"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <FileText size={16} style={{ color: '#ef4444' }} />
        <span>{data.label || 'SAR/STR Draft'}</span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function MonitoringNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.monitoring,
        boxShadow: selected ? '0 0 0 2px var(--accent-secondary)' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="monitoring"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Eye size={16} style={{ color: 'var(--accent-secondary)' }} />
        <span>{data.label || 'Continuous Monitoring'}</span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

function IntegrationNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.integration,
        boxShadow: selected ? '0 0 0 2px #10b981' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="integration"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Activity size={16} style={{ color: '#10b981' }} />
        <span>{data.label || 'External API Check'}</span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

/**
 * Review Step Node - Sumsub-style manual review routing
 * Routes applicant to compliance team for human review
 * Shows clear visual indication this is a MANUAL step
 */
function ReviewStepNode({ data, selected, id }) {
  return (
    <motion.div
      initial="initial"
      animate={selected ? "selected" : "initial"}
      whileHover="hover"
      whileTap="tap"
      variants={nodeAnimationVariants}
      style={{
        ...nodeStyles.base,
        ...nodeStyles.review,
        boxShadow: selected ? '0 0 0 2px #a855f7' : 'none',
      }}
      data-testid={`workflow-node-${id}`}
      data-node-type="review"
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Eye size={16} style={{ color: '#a855f7' }} />
        <span>{data.label || 'Review Step'}</span>
        <span style={{
          padding: '2px 6px',
          background: 'rgba(168, 85, 247, 0.2)',
          borderRadius: '4px',
          fontSize: '9px',
          fontWeight: '600',
          color: '#a855f7',
          textTransform: 'uppercase',
        }}>
          Manual
        </span>
      </div>
      {data.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {data.description}
        </div>
      )}
      {data.reviewType && (
        <div style={{
          fontSize: '11px',
          color: '#a855f7',
          marginTop: '4px',
          fontStyle: 'italic'
        }}>
          → {data.reviewType === 'approve_reject' ? 'Approve / Reject' : 'Requires Action'}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </motion.div>
  );
}

const nodeTypes = {
  start: StartNode,
  end: EndNode,
  step: StepNode,
  action: StepNode,
  condition: ConditionNode,
  parallel: ParallelNode,
  merge: MergeNode,
  screening: ScreeningNode,
  escalation: EscalationNode,
  reporting: ReportingNode,
  monitoring: MonitoringNode,
  integration: IntegrationNode,
  review: ReviewStepNode,
};

// ============================================
// WORKFLOW TEMPLATES - Industry Marketplace
// ============================================

// Template categories for organization (matching Sumsub: KYC, KYB, Gambling, Finance)
const TEMPLATE_CATEGORIES = {
  crypto: { name: 'Crypto & Web3', icon: Wallet, color: '#f59e0b' },
  banking: { name: 'Banking & Finance', icon: Shield, color: '#3b82f6' },
  fintech: { name: 'FinTech', icon: Activity, color: '#10b981' },
  kyb: { name: 'KYB (Business)', icon: FileText, color: '#6366f1' },
  gaming: { name: 'Gaming & Gambling', icon: Layers, color: '#ec4899' },
  monitoring: { name: 'Transaction Monitoring', icon: Eye, color: '#8b5cf6' },
};

// Templates are now fetched from the backend API (single source of truth)
// See: backend/app/workers/workflow_templates/ for template definitions
// To add new templates, create them in the backend and they'll appear here automatically

// ============================================
// COMPONENT LIBRARY
// ============================================

// Category display names and order
const CATEGORY_CONFIG = {
  data_collection: { name: 'Data Collection', order: 1 },
  identity: { name: 'Identity Verification', order: 2 },
  compliance: { name: 'Compliance Checks', order: 3 },
  address: { name: 'Address Verification', order: 4 },
  crypto_compliance: { name: 'Crypto Compliance', order: 5 },
  logic: { name: 'Logic & Flow', order: 6 },
  agent: { name: 'AI Agent', order: 7 },
};

// Fallback components if API is unavailable
const FALLBACK_CATEGORIES = [
  {
    id: 'data_collection',
    name: 'Data Collection',
    components: [
      { id: 'consent', name: 'Data Consent', description: 'GDPR/CCPA consent collection' },
      { id: 'email', name: 'Email Only', description: 'Collect email address for contact' },
      { id: 'personal-info', name: 'Personal Information', description: 'Collect name, DOB, nationality, phone' },
      { id: 'edd-questionnaire', name: 'EDD Questionnaire', description: 'Enhanced due diligence questions' },
      { id: 'cdd-questionnaire', name: 'CDD Questionnaire', description: 'Due diligence with risk scoring. Supports FinCEN, Crypto, Banking templates.' },
    ],
  },
  {
    id: 'identity',
    name: 'Identity Verification',
    components: [
      { id: 'id-document', name: 'ID Document', description: "Passport, driver's license, or national ID" },
      { id: 'selfie', name: 'Selfie Capture', description: 'Take a photo for face matching' },
      { id: 'liveness', name: 'Liveness Check', description: 'Verify user is a real person' },
      { id: 'face-match', name: 'Face Match', description: 'Compare selfie to ID photo (runs automatically)' },
    ],
  },
  {
    id: 'compliance',
    name: 'Compliance Checks',
    components: [
      { id: 'sanctions', name: 'Sanctions Screening', description: 'OFAC, UN, EU sanctions lists (runs automatically)' },
      { id: 'pep', name: 'PEP Screening', description: 'Politically exposed persons check (runs automatically)' },
      { id: 'adverse-media', name: 'Adverse Media', description: 'News and media monitoring (runs automatically)' },
    ],
  },
  {
    id: 'address',
    name: 'Address Verification',
    components: [
      { id: 'address', name: 'Address Input', description: 'Collect user address with autocomplete' },
      { id: 'utility-bill', name: 'Proof of Address', description: 'Upload proof of address (utility bill, bank statement, government letter)' },
    ],
  },
  {
    id: 'crypto_compliance',
    name: 'Crypto Compliance',
    components: [
      { id: 'wallet-address', name: 'Wallet Address', description: 'Collect crypto wallet address for sanctions screening' },
      { id: 'wallet-screening', name: 'Wallet Screening', description: 'Screen wallet via Chainalysis (runs automatically)' },
    ],
  },
  {
    id: 'logic',
    name: 'Logic & Flow',
    components: [
      { id: 'condition', name: 'Condition', description: 'Branch based on rules (multi-route supported)' },
      { id: 'parallel', name: 'Parallel', description: 'Run steps simultaneously' },
      { id: 'merge', name: 'Merge', description: 'Join branches back together (wait for all or first)' },
      { id: 'manual-review', name: 'Manual Review', description: 'Queue for human review' },
      { id: 'review-step', name: 'Review Step', description: 'Route to compliance for manual approval/rejection (like Sumsub)' },
      { id: 'delay', name: 'Delay', description: 'Wait before continuing' },
    ],
  },
];

function ComponentLibrary({ onDragStart, collapsed, onToggle, onBack }) {
  const [search, setSearch] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('identity');

  // Fetch components from API
  const { data: componentsData, isLoading, error } = useComponents();

  // Transform API response to category array format
  const componentCategories = useMemo(() => {
    if (!componentsData?.categories) {
      return FALLBACK_CATEGORIES;
    }

    // Convert API response (categories object) to array format
    const categories = Object.entries(componentsData.categories).map(([categoryId, components]) => ({
      id: categoryId,
      name: CATEGORY_CONFIG[categoryId]?.name || categoryId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      order: CATEGORY_CONFIG[categoryId]?.order || 99,
      components: components.map(comp => ({
        id: comp.component_id,
        name: comp.name,
        description: comp.description || '',
        icon: comp.icon,
        isUserFacing: comp.is_user_facing,
        configSchema: comp.config_schema,
        defaultConfig: comp.default_config,
      })),
    }));

    // Sort by configured order
    return categories.sort((a, b) => a.order - b.order);
  }, [componentsData]);

  const filteredCategories = useMemo(() => {
    if (!search) return componentCategories;
    const lower = search.toLowerCase();
    return componentCategories
      .map((cat) => ({
        ...cat,
        components: cat.components.filter(
          (c) =>
            c.name.toLowerCase().includes(lower) ||
            (c.description && c.description.toLowerCase().includes(lower))
        ),
      }))
      .filter((cat) => cat.components.length > 0);
  }, [search, componentCategories]);

  if (collapsed) {
    return (
      <div className="component-library collapsed">
        <button className="toggle-btn" onClick={onToggle}>
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="component-library">
      {/* Back to Workflows button */}
      {onBack && (
        <button className="back-to-workflows-btn" onClick={onBack}>
          <ChevronLeft size={14} />
          <span>Back to Workflows</span>
        </button>
      )}
      <div className="library-header">
        <h3>Components</h3>
        <button className="toggle-btn" onClick={onToggle}>
          <ChevronLeft size={16} />
        </button>
      </div>

      <div className="search-box">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search components..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')}>
            <X size={14} />
          </button>
        )}
      </div>

      <div className="categories">
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '8px', fontSize: '12px' }}>Loading components...</p>
          </div>
        ) : error ? (
          <div style={{ padding: '16px', color: 'var(--error)', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ marginBottom: '4px' }} />
            <p>Failed to load components. Using defaults.</p>
          </div>
        ) : filteredCategories.map((category) => (
          <div key={category.id} className="category">
            <button
              className="category-header"
              onClick={() =>
                setExpandedCategory(expandedCategory === category.id ? null : category.id)
              }
            >
              <span>{category.name}</span>
              {expandedCategory === category.id ? (
                <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} />
              ) : (
                <ChevronRight size={14} style={{ transform: 'rotate(90deg)' }} />
              )}
            </button>

            {expandedCategory === category.id && (
              <div className="components">
                {category.components.map((component) => {
                  const Icon = componentIcons[component.id] || FileText;
                  return (
                    <div
                      key={component.id}
                      className="component-item"
                      draggable
                      onDragStart={(e) => onDragStart(e, component)}
                    >
                      <Icon size={16} />
                      <div className="component-info">
                        <span className="component-name">{component.name}</span>
                        <span className="component-desc">{component.description}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// AGENT NODE CONFIG (checklist for the review node)
// ============================================

// The checklist sections the AI Analyst can run. This makes an agent_review node
// a configurable RECIPE: pick the whole checklist, or just specific sections.
const REVIEW_SECTION_OPTIONS = [
  { key: 'identity', label: 'Identification & Verification', hint: 'ID validity, tampering, selfie/liveness, name/DOB match' },
  { key: 'proof_of_address', label: 'Proof of Address', hint: 'Acceptable PoA, recency (90 days), verification' },
  { key: 'screening', label: 'Screening', hint: 'Sanctions / watchlist / PEP / adverse media' },
  { key: 'source_of_funds', label: 'Source of Funds', hint: 'EDD industry / unclear source' },
  { key: 'tax_and_disclosures', label: 'Tax & Self-Disclosures', hint: 'PEP / legal actions / tax residency / high-risk industry' },
  { key: 'jurisdiction', label: 'Jurisdiction', hint: 'Prohibited / EDD / high-risk country' },
  { key: 'device', label: 'Device & Location', hint: 'IP jurisdiction match, VPN/proxy/Tor signals' },
  { key: 'wallet', label: 'Wallet / Crypto', hint: 'Sanctions / darknet / mixer screening' },
];

const ALL_SECTION_KEYS = REVIEW_SECTION_OPTIONS.map((s) => s.key);

function AgentNodeConfig({ node, onUpdate }) {
  const componentType = String(node.data.componentType || node.data.componentId || '');
  const config = node.data.config || {};

  const setConfig = (patch) => {
    onUpdate({
      ...node,
      data: { ...node.data, config: { ...config, ...patch } },
    });
  };

  // Only the review node carries a checklist; other agent nodes get a short note.
  if (componentType !== 'agent_review') {
    return (
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: 600 }}>
          <Bot size={14} style={{ color: '#14b8a6' }} /> AI Agent step
        </label>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          This step is performed by the AI agent and recorded to the tamper-evident audit log.
          Recommend-only: escalations route to a human reviewer.
        </div>
      </div>
    );
  }

  // undefined / empty sections = the full checklist (matches the backend default).
  const selected = Array.isArray(config.sections) && config.sections.length > 0
    ? config.sections
    : ALL_SECTION_KEYS;

  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...ALL_SECTION_KEYS.filter((k) => selected.includes(k) || k === key)];
    // Keep at least one section selected.
    setConfig({ sections: next.length ? next : [key] });
  };

  return (
    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', fontWeight: 600 }}>
        <Bot size={14} style={{ color: '#14b8a6' }} /> Review checklist
      </label>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
        Choose what this review checks. This is the recipe: run the whole checklist, or only specific sections.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {REVIEW_SECTION_OPTIONS.map((s) => {
          const on = selected.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${on ? '#14b8a6' : 'var(--border-color)'}`,
                background: on ? 'rgba(20, 184, 166, 0.08)' : 'var(--bg-tertiary)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  marginTop: '1px',
                  flexShrink: 0,
                  border: `1.5px solid ${on ? '#14b8a6' : 'var(--border-color)'}`,
                  background: on ? '#14b8a6' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {on && <Check size={12} style={{ color: '#fff' }} />}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{s.label}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.hint}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px' }}>
        {selected.length === ALL_SECTION_KEYS.length
          ? 'Full checklist (all sections).'
          : `${selected.length} of ${ALL_SECTION_KEYS.length} sections selected.`}
      </div>
    </div>
  );
}

// ============================================
// NODE CONFIG PANEL
// ============================================

function NodeConfigPanel({ node, onUpdate, onClose, onDelete }) {
  if (!node) return null;

  const canDelete = node.type !== 'start' && node.type !== 'end';

  const handleChange = (field, value) => {
    onUpdate({
      ...node,
      data: {
        ...node.data,
        [field]: value,
      },
    });
  };

  return (
    <motion.div
      className="config-panel"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={panelSlideVariants}
    >
      <div className="panel-header">
        <h3>Configure Node</h3>
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X size={16} />
        </motion.button>
      </div>

      <div className="panel-content">
        <motion.div
          className="form-group"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label>Label</label>
          <input
            type="text"
            value={node.data.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
          />
        </motion.div>

        <motion.div
          className="form-group"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <label>Description</label>
          <textarea
            value={node.data.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
          />
        </motion.div>

        {String(node.data.componentType || node.data.componentId || '').startsWith('agent_') && (
          <AgentNodeConfig node={node} onUpdate={onUpdate} />
        )}

        {node.type === 'condition' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontWeight: '600' }}>
              <GitBranch size={14} />
              Route Conditions
            </label>

            {/* Visual Expression Builder */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                Build your condition visually:
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <select
                  value={node.data.conditionField || ''}
                  onChange={(e) => handleChange('conditionField', e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: '100px',
                    padding: '8px 10px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">Select field...</option>
                  <option value="risk_score">Risk Score</option>
                  <option value="country">Country</option>
                  <option value="document_verified">Document Verified</option>
                  <option value="liveness_passed">Liveness Passed</option>
                  <option value="sanctions_hit">Sanctions Hit</option>
                  <option value="pep_hit">PEP Hit</option>
                  <option value="adverse_media_hit">Adverse Media Hit</option>
                  <option value="wallet_risk_score">Wallet Risk Score</option>
                  <option value="applicant_type">Applicant Type</option>
                </select>
                <select
                  value={node.data.conditionOperator || ''}
                  onChange={(e) => handleChange('conditionOperator', e.target.value)}
                  style={{
                    width: '80px',
                    padding: '8px 10px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">=</option>
                  <option value="==">equals</option>
                  <option value="!=">not equals</option>
                  <option value=">">{'>'}</option>
                  <option value=">=">{'≥'}</option>
                  <option value="<">{'<'}</option>
                  <option value="<=">{'≤'}</option>
                  <option value="contains">contains</option>
                </select>
                <input
                  type="text"
                  value={node.data.conditionValue || ''}
                  onChange={(e) => handleChange('conditionValue', e.target.value)}
                  placeholder="Value"
                  style={{
                    flex: 1,
                    minWidth: '60px',
                    padding: '8px 10px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              {/* Generate expression from visual builder */}
              {node.data.conditionField && node.data.conditionOperator && node.data.conditionValue && (
                <div style={{
                  padding: '8px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  color: 'var(--accent-primary)',
                }}>
                  {`${node.data.conditionField} ${node.data.conditionOperator} ${node.data.conditionValue}`}
                </div>
              )}
            </div>

            {/* Or use advanced expression */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Or write a custom expression:
              </div>
              <input
                type="text"
                value={node.data.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                placeholder="e.g., risk_score > 60 && country != 'US'"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* Multi-route configuration */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600' }}>Routes</span>
                <button
                  onClick={() => {
                    const routes = node.data.routes || [
                      { id: 'yes', name: 'Yes', condition: node.data.condition },
                      { id: 'no', name: 'No (else)', isElse: true },
                    ];
                    const newRoute = {
                      id: `route_${Date.now()}`,
                      name: `Route ${routes.length}`,
                      condition: '',
                    };
                    // Insert before the else route
                    const elseIndex = routes.findIndex(r => r.isElse);
                    const newRoutes = elseIndex >= 0
                      ? [...routes.slice(0, elseIndex), newRoute, ...routes.slice(elseIndex)]
                      : [...routes, newRoute];
                    handleChange('routes', newRoutes);
                  }}
                  style={{
                    padding: '4px 8px',
                    background: 'var(--accent-primary)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  + Add Route
                </button>
              </div>
              {(node.data.routes || [{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No (else)', isElse: true }]).map((route, index) => (
                <div
                  key={route.id}
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginBottom: '8px',
                    padding: '8px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '6px',
                  }}
                >
                  <input
                    type="text"
                    value={route.name}
                    onChange={(e) => {
                      const routes = [...(node.data.routes || [{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No (else)', isElse: true }])];
                      routes[index] = { ...routes[index], name: e.target.value };
                      handleChange('routes', routes);
                    }}
                    placeholder="Route name"
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '11px',
                      color: 'var(--text-primary)',
                    }}
                  />
                  {route.isElse ? (
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      (else)
                    </span>
                  ) : (
                    <input
                      type="text"
                      value={route.condition || ''}
                      onChange={(e) => {
                        const routes = [...(node.data.routes || [{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No (else)', isElse: true }])];
                        routes[index] = { ...routes[index], condition: e.target.value };
                        handleChange('routes', routes);
                      }}
                      placeholder="condition"
                      style={{
                        flex: 2,
                        padding: '6px 8px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        color: 'var(--text-primary)',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Merge Node Configuration */}
        {node.type === 'merge' && (
          <motion.div
            className="form-group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <GitBranch size={14} style={{ transform: 'rotate(180deg)' }} />
              Merge Mode
            </label>
            <select
              value={node.data.mode || 'all'}
              onChange={(e) => handleChange('mode', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '13px',
                color: 'var(--text-primary)',
              }}
            >
              <option value="all">Wait for ALL branches (AND)</option>
              <option value="any">Continue on FIRST complete (OR)</option>
            </select>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
              {node.data.mode === 'any'
                ? 'Flow continues as soon as any connected branch completes.'
                : 'Flow waits until all connected branches complete before continuing.'}
            </div>
          </motion.div>
        )}

        {/* Trigger Configuration for Start Nodes */}
        {node.type === 'start' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontWeight: '600' }}>
              <Clock size={14} />
              Trigger Configuration
            </label>

            <div className="form-group">
              <label>Trigger Type</label>
              <select
                value={node.data.trigger_type || 'event'}
                onChange={(e) => handleChange('trigger_type', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="event">Event (applicant.created)</option>
                <option value="schedule">Scheduled</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>

            {node.data.trigger_type === 'schedule' && (
              <>
                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    value={node.data.frequency || 'daily'}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly (90 days)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Run Time (UTC)</label>
                  <input
                    type="time"
                    value={node.data.run_time || '02:00'}
                    onChange={(e) => handleChange('run_time', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Target Applicants</label>
                  <select
                    value={node.data.target_filter || 'approved'}
                    onChange={(e) => handleChange('target_filter', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="approved">Approved Only</option>
                    <option value="approved_review">Approved + In Review</option>
                    <option value="all">All Applicants</option>
                  </select>
                </div>

                <div style={{
                  padding: '10px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#8b5cf6',
                  marginTop: '8px',
                }}>
                  <strong>Note:</strong> Scheduled workflows skip applicants that have been monitored within the frequency period (last_monitored_at check).
                </div>
              </>
            )}
          </motion.div>
        )}

        {!['start', 'end', 'condition'].includes(node.type) && (
          <>
            {node.data.component_id === 'cdd_questionnaire' && (
              <CddQuestionnaireConfig node={node} onUpdate={onUpdate} />
            )}

            {['sanctions_check', 'sanctions_screening', 'sanctions', 'run_screening'].includes(node.data.component_id) && (
              <SanctionsNodeConfig node={node} onUpdate={onUpdate} />
            )}

            {/* Backwards-compatible check for liveness and document nodes */}
            {(
              ['liveness', 'id_document', 'document', 'id_doc'].includes(node.data.component_id || node.data.componentType || '') ||
              ['liveness', 'id-doc', 'id_doc', 'document'].includes(node.id || '') ||
              (node.data.label || '').toLowerCase().includes('liveness') ||
              (node.data.label || '').toLowerCase().includes('id document')
            ) && (
              <VerificationNodeConfig node={node} onUpdate={onUpdate} />
            )}

            {!['cdd_questionnaire', 'sanctions_check', 'sanctions_screening', 'sanctions', 'run_screening'].includes(node.data.component_id) && (
              <>
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <label>Required</label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={node.data.required !== false}
                      onChange={(e) => handleChange('required', e.target.checked)}
                    />
                    <span>This step is required</span>
                  </label>
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <label>Timeout (seconds)</label>
                  <input
                    type="number"
                    value={node.data.timeout || 300}
                    onChange={(e) => handleChange('timeout', parseInt(e.target.value))}
                  />
                </motion.div>
              </>
            )}
          </>
        )}

        {canDelete && (
          <motion.div
            className="form-group delete-section"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.button
              className="delete-node-btn"
              onClick={() => onDelete(node.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Trash2 size={14} />
              Delete Node
            </motion.button>
            <span className="delete-hint">or press Delete key</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// WORKFLOW LIST
// ============================================

// Transform nodes to match backend schema (component_id, name)
// Only includes stable properties to avoid constant re-saves from selection/drag state
const transformNodesForBackend = (nodes) => {
  if (!nodes) return [];
  return nodes.map(node => ({
    id: node.id,
    type: node.type,
    position: node.position,
    data: {
      ...node.data,
      component_id: node.data.componentType || node.data.component_id || node.type,
      name: node.data.label || node.data.name || node.type,
    }
  }));
};

// Transform nodes from backend/AI schema to frontend schema (label, componentType)
const transformNodesFromBackend = (nodes) => {
  if (!nodes) return [];
  return nodes.map(node => {
    const data = node.data || {};
    return {
      ...node,
      data: {
        ...data,
        // Map backend field names to frontend field names
        // AI uses: name, componentId (camelCase) or component_id (snake_case)
        // Frontend expects: label, componentType
        label: data.label || data.name || data.componentId || data.component_id || node.type || 'Untitled',
        description: data.description || '',
        componentType: data.componentType || data.componentId || data.component_id || node.type || 'step',
      }
    };
  });
};

// Transform edges from backend schema to frontend schema
// Handles both camelCase (sourceHandle) and snake_case (source_handle) field names
// Also handles legacy edges that use data.condition instead of sourceHandle/label
const transformEdgesFromBackend = (edges) => {
  if (!edges) return [];
  return edges.map(edge => {
    // Start with existing sourceHandle/label
    let sourceHandle = edge.sourceHandle || edge.source_handle;
    let label = edge.label || edge.data?.label;

    // Handle legacy AI edges that use data.condition instead of sourceHandle/label
    // This ensures backward compatibility with existing workflows
    if (!sourceHandle && edge.data?.condition !== undefined && edge.data?.condition !== null) {
      const conditionValue = edge.data.condition;
      const isTrue = conditionValue === 'true' || conditionValue === true;
      sourceHandle = isTrue ? 'yes' : 'no';
      // Only set label if not already present
      if (!label) {
        label = isTrue ? 'Yes' : 'No';
      }
    }

    return {
      ...edge,
      sourceHandle: sourceHandle || null,
      targetHandle: edge.targetHandle || edge.target_handle || null,
      label: label || null,
    };
  });
};

// Map backend industry string to frontend category key
// Uses the FIRST industry in the comma-separated list for primary categorization
const mapIndustryToCategory = (industry) => {
  if (!industry) return 'crypto';
  // Get the first industry (primary) for categorization
  const primaryIndustry = industry.split(',')[0].trim().toLowerCase();

  // Map primary industry to category
  if (primaryIndustry === 'fintech' || primaryIndustry === 'neobank') return 'fintech';
  if (primaryIndustry === 'banking' || primaryIndustry === 'lending' || primaryIndustry === 'payments') return 'banking';
  if (primaryIndustry === 'gaming' || primaryIndustry === 'gambling' || primaryIndustry === 'betting' || primaryIndustry === 'casino') return 'gaming';
  if (primaryIndustry === 'kyb' || primaryIndustry === 'business' || primaryIndustry === 'b2b' || primaryIndustry === 'enterprise') return 'kyb';
  if (primaryIndustry === 'defi' || primaryIndustry === 'crypto' || primaryIndustry === 'web3' || primaryIndustry === 'exchange') return 'crypto';
  if (primaryIndustry === 'monitoring' || primaryIndustry === 'aml' || primaryIndustry === 'transaction') return 'monitoring';

  // Fallback: check if any industry matches (for templates with multiple industries)
  const ind = industry.toLowerCase();
  if (ind.includes('fintech')) return 'fintech';
  if (ind.includes('bank')) return 'banking';
  if (ind.includes('gam') || ind.includes('betting') || ind.includes('casino')) return 'gaming';
  if (ind.includes('kyb') || ind.includes('business')) return 'kyb';
  if (ind.includes('defi') || ind.includes('crypto') || ind.includes('web3')) return 'crypto';

  return 'crypto'; // Default fallback
};

function WorkflowList({ onSelect, onImportClick }) {
  const { data: workflows, isLoading } = useWorkflows();
  const { data: apiTemplatesData } = useTemplates(); // Fetch templates from backend API
  const createWorkflow = useCreateWorkflow();
  const deleteWorkflow = useDeleteWorkflow();
  const unpublishWorkflow = useUnpublishWorkflow();
  const createLiveLink = useCreateLiveLink();
  const toast = useToast();

  // Live link modal state
  const [linkModal, setLinkModal] = useState(null); // { workflowName, url, expiresAt }
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLoadingId, setLinkLoadingId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(null); // null = all industries
  const [selectedType, setSelectedType] = useState(null); // null = all types (onboarding, post_onboarding, monitoring, kyb)

  // Convert API templates to frontend format - single source of truth from backend
  const templates = useMemo(() => {
    if (!apiTemplatesData?.items) return [];
    return apiTemplatesData.items.map(t => ({
      id: t.template_id,
      name: t.name,
      description: t.description,
      workflow_type: t.workflow_type || t.category,
      category: mapIndustryToCategory(t.industry),
      industries: t.industry?.split(',') || [],
      nodes: t.config?.nodes || [],
      edges: t.config?.edges || [],
      triggers: t.config?.triggers,
    }));
  }, [apiTemplatesData]);

  const [showNameModal, setShowNameModal] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const nameInputRef = useRef(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);

  // Unpublish confirmation modal state
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [workflowToUnpublish, setWorkflowToUnpublish] = useState(null);

  // Focus input when modal opens
  useEffect(() => {
    if (showNameModal && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [showNameModal]);

  const handleCreate = async (template = null) => {
    if (template) {
      // Template has auto-generated name
      const name = `${template.name} - ${new Date().toLocaleDateString()}`;
      const type = template.workflow_type || 'onboarding';
      try {
        const workflow = await createWorkflow.mutateAsync({
          name,
          workflow_type: type,
          description: template.description || '',
          config: {
            nodes: transformNodesForBackend(template.nodes),
            edges: template.edges || []
          },
        });
        onSelect(workflow.id);
        setShowTemplates(false);
        toast.success('Workflow created from template');
      } catch (error) {
        toast.error('Failed to create workflow: ' + error.message);
      }
    } else {
      // Show modal for blank workflow name
      setWorkflowName('');
      setShowNameModal(true);
    }
  };

  const handleCreateBlank = async () => {
    if (!workflowName.trim()) return;

    try {
      const workflow = await createWorkflow.mutateAsync({
        name: workflowName.trim(),
        workflow_type: 'onboarding',
        description: '',
      });
      onSelect(workflow.id);
      setShowNameModal(false);
      setWorkflowName('');
      toast.success('Workflow created');
    } catch (error) {
      toast.error('Failed to create workflow: ' + error.message);
    }
  };

  const handleDelete = (workflow, e) => {
    e.stopPropagation();
    setWorkflowToDelete(workflow);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!workflowToDelete) return;
    try {
      await deleteWorkflow.mutateAsync(workflowToDelete.id);
      toast.success('Workflow deleted');
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
    } catch (error) {
      // Backend returns a 400 with an actionable detail (e.g. published workflows
      // must be unpublished first). Surface that reason instead of a bare status code.
      const detail = error?.data?.detail || error?.message || '';
      const isPublishedBlock = error?.status === 400 && /publish/i.test(detail);
      toast.error(
        isPublishedBlock
          ? 'This workflow is published. Unpublish it first (workflow menu → Unpublish), then delete.'
          : 'Failed to delete workflow: ' + (detail || 'unknown error')
      );
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setWorkflowToDelete(null);
  };

  const handleUnpublish = (workflow, e) => {
    e.stopPropagation();
    setWorkflowToUnpublish(workflow);
    setShowUnpublishModal(true);
  };

  const confirmUnpublish = async () => {
    if (!workflowToUnpublish) return;
    try {
      await unpublishWorkflow.mutateAsync(workflowToUnpublish.id);
      toast.success('Workflow unpublished');
      setShowUnpublishModal(false);
      setWorkflowToUnpublish(null);
    } catch (error) {
      toast.error('Failed to unpublish workflow: ' + error.message);
      setShowUnpublishModal(false);
      setWorkflowToUnpublish(null);
    }
  };

  const cancelUnpublish = () => {
    setShowUnpublishModal(false);
    setWorkflowToUnpublish(null);
  };

  const handleGetLink = async (workflow, e) => {
    e.stopPropagation();
    setLinkLoadingId(workflow.id);
    try {
      const result = await createLiveLink.mutateAsync({ workflowName: workflow.name });
      setLinkCopied(false);
      setLinkModal({
        workflowName: result.workflow_name || workflow.name,
        url: result.url,
        expiresAt: result.expires_at,
      });
    } catch (error) {
      const detail = error?.data?.detail || error?.message || 'unknown error';
      toast.error('Failed to generate link: ' + detail);
    } finally {
      setLinkLoadingId(null);
    }
  };

  const copyLink = async () => {
    if (!linkModal?.url) return;
    try {
      await navigator.clipboard.writeText(linkModal.url);
      setLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select and copy the link manually.');
    }
  };

  if (isLoading) {
    return (
      <div className="workflow-list loading">
        <Loader2 size={24} className="spin" />
        <span>Loading workflows...</span>
      </div>
    );
  }

  return (
    <div className="workflow-list" data-testid="workflow-list">
      <div className="list-header">
        {/* View Toggle - Workflows vs Templates */}
        <div style={{ display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '3px', flexShrink: 0 }}>
          <button
            onClick={() => setShowTemplates(false)}
            style={{
              padding: '8px 12px',
              minWidth: '120px',
              background: !showTemplates ? 'var(--bg-secondary)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: !showTemplates ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontSize: '12px',
              fontWeight: !showTemplates ? '600' : '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: !showTemplates ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <LayoutGrid size={14} style={{ flexShrink: 0 }} />
            <span>Workflows</span>
            <span style={{
              padding: '1px 6px',
              background: !showTemplates ? 'var(--accent-glow-medium)' : 'var(--bg-secondary)',
              color: !showTemplates ? 'var(--accent-primary)' : 'var(--text-muted)',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600',
              minWidth: '20px',
              textAlign: 'center',
            }}>
              {workflows?.items?.length || 0}
            </span>
          </button>
          <button
            onClick={() => setShowTemplates(true)}
            style={{
              padding: '8px 12px',
              minWidth: '120px',
              background: showTemplates ? 'var(--bg-secondary)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: showTemplates ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontSize: '12px',
              fontWeight: showTemplates ? '600' : '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: showTemplates ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <Layers size={14} style={{ flexShrink: 0 }} />
            <span>Templates</span>
            <span style={{
              padding: '1px 6px',
              background: showTemplates ? 'var(--accent-glow-medium)' : 'var(--bg-secondary)',
              color: showTemplates ? 'var(--accent-primary)' : 'var(--text-muted)',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600',
              minWidth: '20px',
              textAlign: 'center',
            }}>
              {templates.length}
            </span>
          </button>
        </div>
        {/* Create New Button */}
        <button className="create-btn" onClick={() => handleCreate()} disabled={createWorkflow.isPending} data-testid="create-workflow-btn" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {createWorkflow.isPending ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
          Blank
        </button>
        {/* Import from Screenshot Button */}
        <button
          className="import-btn"
          onClick={onImportClick}
          data-testid="import-workflow-btn"
          style={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-secondary)',
            padding: '8px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.15s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent-primary)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-color)';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }}
          title="Import workflow from screenshot (Persona, Sumsub, etc.)"
        >
          <Upload size={14} />
          Import
        </button>
      </div>

      {showTemplates && (
        <div className="list-content" data-testid="templates-browser" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Filter Bar - Fixed, doesn't scroll */}
          <div style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px', marginBottom: '12px', flexShrink: 0 }}>
            {/* Workflow Type Filter */}
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                Workflow Type
              </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                { key: null, label: 'All', count: templates.length },
                { key: 'onboarding', label: 'Onboarding', count: templates.filter(t => t.workflow_type === 'onboarding').length },
                { key: 'post_onboarding', label: 'Post-Onboarding', count: templates.filter(t => t.workflow_type === 'post_onboarding').length },
                { key: 'kyb', label: 'KYB', count: templates.filter(t => t.workflow_type === 'kyb').length },
                { key: 'monitoring', label: 'Monitoring', count: templates.filter(t => t.workflow_type === 'monitoring').length },
              ].map(({ key, label, count }) => (
                <button
                  key={label}
                  onClick={() => setSelectedType(key)}
                  style={{
                    padding: '6px 10px',
                    background: selectedType === key ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                    color: selectedType === key ? 'white' : 'var(--text-primary)',
                    border: `1px solid ${selectedType === key ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {label}
                  <span style={{
                    padding: '1px 5px',
                    background: selectedType === key ? 'rgba(255,255,255,0.2)' : 'var(--bg-tertiary)',
                    borderRadius: '8px',
                    fontSize: '9px',
                  }}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Industry Filter */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Industry
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setSelectedIndustry(null)}
                style={{
                  padding: '6px 10px',
                  background: selectedIndustry === null ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                  color: selectedIndustry === null ? 'white' : 'var(--text-primary)',
                  border: `1px solid ${selectedIndustry === null ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                All Industries
              </button>
              {Object.entries(TEMPLATE_CATEGORIES).map(([key, { name, icon: Icon, color }]) => {
                const count = templates.filter(t => t.category === key && (!selectedType || t.workflow_type === selectedType)).length;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedIndustry(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      background: selectedIndustry === key ? color : 'var(--bg-secondary)',
                      color: selectedIndustry === key ? 'white' : 'var(--text-primary)',
                      border: `1px solid ${selectedIndustry === key ? color : 'var(--border-color)'}`,
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon size={12} style={{ color: selectedIndustry === key ? 'white' : color }} />
                    {name.replace(' (Business)', '')}
                    <span style={{
                      padding: '1px 5px',
                      background: selectedIndustry === key ? 'rgba(255,255,255,0.2)' : 'var(--bg-tertiary)',
                      borderRadius: '8px',
                      fontSize: '9px',
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          </div>

          {/* Filtered Templates List - Scrollable */}
          <div className="workflow-items" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {(() => {
              const filteredTemplates = templates.filter(t => {
                const matchesType = !selectedType || t.workflow_type === selectedType;
                const matchesIndustry = !selectedIndustry || t.category === selectedIndustry;
                return matchesType && matchesIndustry;
              });

              if (filteredTemplates.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                    <p>No templates match your filters</p>
                    <button
                      onClick={() => { setSelectedType(null); setSelectedIndustry(null); }}
                      style={{ marginTop: '8px', padding: '6px 12px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Clear Filters
                    </button>
                  </div>
                );
              }

              // Color coding by workflow_type (not industry)
              const WORKFLOW_TYPE_COLORS = {
                onboarding: '#10b981',      // green
                post_onboarding: '#f59e0b', // amber/orange
                kyb: '#6366f1',             // indigo
                monitoring: '#8b5cf6',      // purple
              };

              return (
                <div className="template-list" style={{ gap: '8px' }}>
                  {filteredTemplates.map((template) => {
                    const categoryColor = TEMPLATE_CATEGORIES[template.category]?.color || '#666';
                    const CategoryIcon = TEMPLATE_CATEGORIES[template.category]?.icon || Layers;
                    const workflowTypeColor = WORKFLOW_TYPE_COLORS[template.workflow_type] || '#666';
                    return (
                      <div
                        key={template.id}
                        className="template-item"
                        onClick={() => handleCreate(template)}
                        data-testid={`template-item-${template.id}`}
                        style={{
                          borderLeft: `3px solid ${workflowTypeColor}`,
                          paddingLeft: '12px',
                        }}
                      >
                        <div className="template-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <CategoryIcon size={12} style={{ color: categoryColor }} />
                            <span className="template-name">{template.name}</span>
                          </div>
                          <span className="template-desc">{template.description}</span>
                          {template.trigger_config && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginTop: '6px',
                              padding: '2px 8px',
                              background: 'rgba(139, 92, 246, 0.1)',
                              borderRadius: '4px',
                              fontSize: '10px',
                              color: '#8b5cf6',
                            }}>
                              <Clock size={10} />
                              {template.trigger_config.type}: {template.trigger_config.frequency || template.trigger_config.event}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span className={`template-type ${template.workflow_type}`}>
                            {template.workflow_type === 'monitoring' ? 'monitoring' : template.workflow_type.replace('_', ' ')}
                          </span>
                          <button
                            className="use-template-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreate(template);
                            }}
                            style={{
                              padding: '4px 10px',
                              background: categoryColor,
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Use Template
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Workflows list - only show when Templates tab is not selected */}
      {!showTemplates && (
        <div className="list-content">
          {workflows?.items?.length === 0 ? (
            <div className="empty-state">
              <Layers size={48} />
              <h3>No workflows yet</h3>
              <p>Start from a template or create a blank workflow</p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button onClick={() => setShowTemplates(true)}>
                  <Layers size={16} />
                  Use Template
                </button>
                <button onClick={() => handleCreate()} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <Plus size={16} />
                  Blank Workflow
                </button>
              </div>
            </div>
          ) : (
            <div className="workflow-items">
              {workflows?.items?.map((workflow) => (
              <div
                key={workflow.id}
                className="workflow-item"
                onClick={() => onSelect(workflow.id)}
                data-testid={`workflow-item-${workflow.id}`}
              >
                <div className="workflow-info">
                  <span className="workflow-name">{workflow.name}</span>
                  <span className="workflow-type">{workflow.workflow_type}</span>
                </div>
                <div className="workflow-meta">
                  <span className={`status ${workflow.status}`}>{workflow.status}</span>
                  {workflow.status === 'published' && (
                    <button
                      className="row-action-btn get-link-btn"
                      onClick={(e) => handleGetLink(workflow, e)}
                      disabled={linkLoadingId === workflow.id}
                      title="Get a shareable live verification link"
                      data-testid={`get-link-${workflow.id}`}
                    >
                      {linkLoadingId === workflow.id
                        ? <Loader2 size={13} className="spin" />
                        : <Link2 size={13} />}
                      <span>Get link</span>
                    </button>
                  )}
                  {workflow.status === 'published' && (
                    <button
                      className="row-action-btn unpublish-btn"
                      onClick={(e) => handleUnpublish(workflow, e)}
                      title="Unpublish (revert to draft)"
                    >
                      <span>Unpublish</span>
                    </button>
                  )}
                  <button className="delete-btn" onClick={(e) => handleDelete(workflow, e)} title="Delete workflow">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Workflow Name Modal */}
      <AnimatePresence>
        {showNameModal && (
          <motion.div
            className="modal-overlay"
            onClick={() => setShowNameModal(false)}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          >
            <motion.div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              variants={modalVariants}
            >
              <h3>Create New Workflow</h3>
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Enter workflow name..."
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateBlank()}
              />
              <div className="modal-actions">
                <motion.button
                  className="cancel-btn"
                  onClick={() => setShowNameModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  className="create-btn"
                  onClick={handleCreateBlank}
                  disabled={!workflowName.trim() || createWorkflow.isPending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {createWorkflow.isPending ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
                  Create
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <motion.div
            className="modal-overlay"
            onClick={cancelDelete}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          >
            <motion.div
              className="modal-content delete-modal"
              onClick={(e) => e.stopPropagation()}
              variants={modalVariants}
            >
              <div className="delete-modal-icon">
                <Trash2 size={24} />
              </div>
              <h3>Delete Workflow</h3>
              <p>Are you sure you want to delete <strong>"{workflowToDelete?.name}"</strong>?</p>
              <p className="delete-warning">This action cannot be undone.</p>
              <div className="modal-actions">
                <motion.button
                  className="cancel-btn"
                  onClick={cancelDelete}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  className="delete-confirm-btn"
                  onClick={confirmDelete}
                  disabled={deleteWorkflow.isPending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {deleteWorkflow.isPending ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Unpublish Confirmation Modal */}
        {showUnpublishModal && (
          <motion.div
            className="modal-overlay"
            onClick={cancelUnpublish}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          >
            <motion.div
              className="modal-content unpublish-modal"
              onClick={(e) => e.stopPropagation()}
              variants={modalVariants}
            >
              <h3>Unpublish Workflow</h3>
              <p>Are you sure you want to unpublish <strong>"{workflowToUnpublish?.name}"</strong>?</p>
              <p className="unpublish-info">It will revert to draft status and no longer be available for execution.</p>
              <div className="modal-actions">
                <motion.button
                  className="cancel-btn"
                  onClick={cancelUnpublish}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  className="unpublish-confirm-btn"
                  onClick={confirmUnpublish}
                  disabled={unpublishWorkflow.isPending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {unpublishWorkflow.isPending ? <Loader2 size={14} className="spin" /> : null}
                  Unpublish
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Live Link Modal */}
        {linkModal && (
          <motion.div
            className="modal-overlay"
            onClick={() => setLinkModal(null)}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          >
            <motion.div
              className="modal-content live-link-modal"
              onClick={(e) => e.stopPropagation()}
              variants={modalVariants}
              style={{ maxWidth: '520px' }}
            >
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Link2 size={18} /> Live verification link
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                Share this link to run <strong>"{linkModal.workflowName}"</strong> as the applicant
                sees it. No login required.
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  margin: '12px 0',
                }}
              >
                <input
                  readOnly
                  value={linkModal.url}
                  onClick={(e) => e.target.select()}
                  data-testid="live-link-url"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={copyLink}
                  title="Copy link"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '5px 10px',
                    background: linkCopied ? '#10b981' : 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: linkCopied ? '#fff' : 'var(--text-primary)',
                  }}
                >
                  {linkCopied ? <Check size={13} /> : <Copy size={13} />}
                  {linkCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {linkModal.expiresAt && (
                <p style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                  Expires {new Date(linkModal.expiresAt).toLocaleString()}
                </p>
              )}
              <div className="modal-actions">
                <motion.button
                  className="cancel-btn"
                  onClick={() => setLinkModal(null)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Close
                </motion.button>
                <motion.a
                  href={linkModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="unpublish-confirm-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    textDecoration: 'none',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ExternalLink size={14} /> Open
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// MAIN WORKFLOW CANVAS
// ============================================

function WorkflowCanvas({ workflowId, pendingConfigUpdate, onConfigApplied, executionPanelOpen, setExecutionPanelOpen, onBack }) {
  const { data: workflow, isLoading } = useWorkflow(workflowId);
  const updateWorkflow = useUpdateWorkflow();
  const publishWorkflow = usePublishWorkflow();
  const previewWorkflow = usePreviewWorkflow();
  const validateWorkflow = useValidateWorkflow();
  const toast = useToast();
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const hasInitializedViewRef = useRef(false);

  // Center workflow once on initial load only
  const centerWorkflowOnce = useCallback(() => {
    if (!hasInitializedViewRef.current) {
      hasInitializedViewRef.current = true;
      fitView({ padding: 0.1, maxZoom: 1, duration: 300 });
    }
  }, [fitView]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  // Tracks the node a freshly-dropped node should auto-connect FROM (the last
  // node you added or clicked). Lets linear flows chain without dragging edges.
  const chainSourceRef = useRef(null);
  const [libraryCollapsed, setLibraryCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 1200
  );

  // Auto-collapse library on narrow screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setLibraryCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [configPanelOpen, setConfigPanelOpen] = useState(false);

  // Auto-collapse library when config panel opens to ensure enough space
  useEffect(() => {
    if (configPanelOpen) {
      setLibraryCollapsed(true);
    }
  }, [configPanelOpen]);

  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Track saved config for change detection
  const lastSavedConfigRef = useRef(null);

  // Context menu state for node copy/delete
  const [contextMenu, setContextMenu] = useState(null);

  // Quick actions bar state
  const [quickActionsVisible, setQuickActionsVisible] = useState(false);
  const [quickActionsPosition, setQuickActionsPosition] = useState(null);

  // Undo/Redo history
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const isUndoRedoRef = useRef(false);
  const MAX_HISTORY = 50;

  const pushHistory = useCallback((newNodes, newEdges) => {
    if (isUndoRedoRef.current) return; // Don't push during undo/redo
    const snapshot = { nodes: JSON.parse(JSON.stringify(newNodes)), edges: JSON.parse(JSON.stringify(newEdges)) };
    // Truncate any forward history
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(snapshot);
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    }
    historyIndexRef.current = historyRef.current.length - 1;
  }, []);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    isUndoRedoRef.current = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setTimeout(() => { isUndoRedoRef.current = false; }, 50);
  }, [setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    isUndoRedoRef.current = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setTimeout(() => { isUndoRedoRef.current = false; }, 50);
  }, [setNodes, setEdges]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Track if workflow has been initialized to avoid auto-save on initial load
  const isInitialized = useRef(false);
  // Track if we've already loaded this specific workflow
  const loadedWorkflowIdRef = useRef(null);

  // Initialize nodes/edges from workflow config - only runs ONCE per workflow
  useEffect(() => {
    // Skip if we've already initialized this workflow (prevents re-layout on auto-save refetch)
    if (loadedWorkflowIdRef.current === workflowId && isInitialized.current) {
      return;
    }

    // IMPORTANT: Don't set default empty nodes while data is still loading!
    // This prevents the race condition where we show empty state before API responds.
    if (isLoading) {
      return;
    }

    if (workflow?.current_config && workflow.current_config.nodes?.length > 0) {
      const config = workflow.current_config;
      let loadedNodes = transformNodesFromBackend(config.nodes);
      const loadedEdges = transformEdgesFromBackend(config.edges || []);

      // Auto-layout if workflow has more than 2 nodes (templates load crowded otherwise)
      // Only on first load - after that, use saved positions
      if (loadedNodes.length > 2 && !loadedWorkflowIdRef.current) {
        loadedNodes = autoLayoutNodes(loadedNodes, loadedEdges, 'TB');
      }

      setNodes(loadedNodes);
      setEdges(loadedEdges);
      loadedWorkflowIdRef.current = workflowId;
      // Initialize saved config reference so we don't show "unsaved" on fresh load
      lastSavedConfigRef.current = JSON.stringify({
        nodes: transformNodesForBackend(loadedNodes),
        edges: loadedEdges
      });
    } else if (!loadedWorkflowIdRef.current) {
      // Default empty workflow - only set AFTER loading completes and there's no config
      const defaultNodes = [
        {
          id: 'start',
          type: 'start',
          position: { x: 250, y: 50 },
          data: { label: 'Start' },
        },
        {
          id: 'end',
          type: 'end',
          position: { x: 250, y: 400 },
          data: { label: 'End' },
        },
      ];
      const defaultEdges = [];
      setNodes(defaultNodes);
      setEdges(defaultEdges);
      loadedWorkflowIdRef.current = workflowId;
      // Initialize saved config reference for default workflow
      lastSavedConfigRef.current = JSON.stringify({
        nodes: transformNodesForBackend(defaultNodes),
        edges: defaultEdges
      });
    }
    // Push initial state to history, mark as initialized, and center the view
    setTimeout(() => {
      if (workflow?.current_config?.nodes?.length > 0 && !isInitialized.current) {
        let historyNodes = transformNodesFromBackend(workflow.current_config.nodes);
        const historyEdges = transformEdgesFromBackend(workflow.current_config.edges || []);
        if (historyNodes.length > 2) {
          historyNodes = autoLayoutNodes(historyNodes, historyEdges, 'TB');
        }
        pushHistory(historyNodes, historyEdges);
      }
      isInitialized.current = true;
      // Center the workflow in the viewport after nodes are rendered
      centerWorkflowOnce();
    }, 500);
  }, [workflow, workflowId, isLoading, setNodes, setEdges, pushHistory, centerWorkflowOnce]);

  // Apply pending config update from AI chat
  useEffect(() => {
    if (pendingConfigUpdate) {
      let newNodes = [];
      let newEdges = [];

      if (pendingConfigUpdate.nodes && Array.isArray(pendingConfigUpdate.nodes)) {
        // Transform nodes from backend/AI schema to frontend schema
        newNodes = transformNodesFromBackend(pendingConfigUpdate.nodes);
      }
      if (pendingConfigUpdate.edges && Array.isArray(pendingConfigUpdate.edges)) {
        newEdges = transformEdgesFromBackend(pendingConfigUpdate.edges);
      }

      // Auto-layout the nodes for clean visual arrangement
      if (newNodes.length > 0) {
        const layoutedNodes = autoLayoutNodes(newNodes, newEdges, 'TB');
        setNodes(layoutedNodes);
      } else {
        setNodes(newNodes);
      }
      setEdges(newEdges);

      // Push to undo history
      const finalNodes = newNodes.length > 0 ? autoLayoutNodes(newNodes, newEdges, 'TB') : newNodes;
      pushHistory(finalNodes, newEdges);

      // Center the workflow after applying new config
      setTimeout(() => centerWorkflowOnce(), 100);

      // Notify parent that config has been applied
      onConfigApplied?.();
    }
  }, [pendingConfigUpdate, setNodes, setEdges, onConfigApplied, pushHistory, centerWorkflowOnce]);

  // Track unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Browser beforeunload warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      onBack?.();
    }
  }, [hasUnsavedChanges, onBack]);

  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedModal(false);
    setHasUnsavedChanges(false);
    onBack?.();
  }, [onBack]);

  const handleSaveAndClose = useCallback(async () => {
    const config = { nodes: transformNodesForBackend(nodes), edges };
    try {
      await updateWorkflow.mutateAsync({
        id: workflowId,
        data: { config },
      });
      setShowUnsavedModal(false);
      setHasUnsavedChanges(false);
      onBack?.();
    } catch (error) {
      toast.error('Failed to save: ' + error.message);
    }
  }, [nodes, edges, workflowId, updateWorkflow, onBack, toast]);

  // Track changes and push undo history when nodes or edges change (after initialization)
  // Note: We do NOT auto-save - user must click Save button explicitly
  useEffect(() => {
    if (isInitialized.current && nodes.length > 0) {
      // Create config snapshot for comparison
      const currentConfig = JSON.stringify({
        nodes: transformNodesForBackend(nodes),
        edges
      });

      // Only mark as changed if config actually changed (not just array reference)
      if (currentConfig !== lastSavedConfigRef.current) {
        // Mark as having unsaved changes (but don't auto-save)
        setHasUnsavedChanges(true);
        // Push to undo history (pushHistory already ignores undo/redo actions)
        pushHistory(nodes, edges);
      }
    }
  }, [nodes, edges, pushHistory]);

  // ============================================
  // LIVE VALIDATION
  // ============================================
  const performLocalValidation = useCallback(() => {
    const errors = [];
    const warnings = [];

    // Check for start node
    const startNodes = nodes.filter(n => n.type === 'start');
    if (startNodes.length === 0) {
      errors.push({ type: 'missing_start', message: 'Workflow needs a Start node' });
    } else if (startNodes.length > 1) {
      errors.push({ type: 'multiple_start', message: 'Workflow should have only one Start node' });
    }

    // Check for end node
    const endNodes = nodes.filter(n => n.type === 'end');
    if (endNodes.length === 0) {
      errors.push({ type: 'missing_end', message: 'Workflow needs an End node' });
    }

    // Check for disconnected nodes (nodes with no incoming or outgoing edges)
    const connectedNodeIds = new Set();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    // Count non-start/end nodes (actual workflow steps)
    const workflowSteps = nodes.filter(n => n.type !== 'start' && n.type !== 'end');
    const hasWorkflowSteps = workflowSteps.length > 0;

    nodes.forEach(node => {
      if (node.type === 'start') {
        // Start only needs outgoing edges - but only warn if there are workflow steps to connect
        const hasOutgoing = edges.some(e => e.source === node.id);
        if (!hasOutgoing && hasWorkflowSteps) {
          warnings.push({ type: 'disconnected', nodeId: node.id, message: `Start node has no outgoing connections` });
        }
      } else if (node.type === 'end') {
        // End only needs incoming edges - but only warn if there are workflow steps to connect
        const hasIncoming = edges.some(e => e.target === node.id);
        if (!hasIncoming && hasWorkflowSteps) {
          warnings.push({ type: 'disconnected', nodeId: node.id, message: `End node has no incoming connections` });
        }
      } else {
        // Regular nodes need both incoming and outgoing
        const hasIncoming = edges.some(e => e.target === node.id);
        const hasOutgoing = edges.some(e => e.source === node.id);
        if (!hasIncoming && !hasOutgoing) {
          errors.push({ type: 'disconnected', nodeId: node.id, message: `"${node.data?.label || node.id}" is not connected to the workflow` });
        } else if (!hasIncoming) {
          warnings.push({ type: 'no_incoming', nodeId: node.id, message: `"${node.data?.label || node.id}" has no incoming connection` });
        } else if (!hasOutgoing) {
          warnings.push({ type: 'no_outgoing', nodeId: node.id, message: `"${node.data?.label || node.id}" has no outgoing connection` });
        }
      }
    });

    // Check condition nodes have both true/false paths
    const conditionNodes = nodes.filter(n => n.type === 'condition');
    conditionNodes.forEach(node => {
      const outgoingEdges = edges.filter(e => e.source === node.id);
      const hasYes = outgoingEdges.some(e => e.sourceHandle === 'yes' || e.label?.toLowerCase().includes('yes') || e.label?.toLowerCase().includes('true'));
      const hasNo = outgoingEdges.some(e => e.sourceHandle === 'no' || e.label?.toLowerCase().includes('no') || e.label?.toLowerCase().includes('false'));
      if (outgoingEdges.length > 0 && (!hasYes || !hasNo)) {
        const missingPath = !hasYes ? 'Yes' : 'No';
        warnings.push({
          type: 'condition_incomplete',
          nodeId: node.id,
          missingPath,
          message: `Condition "${node.data?.label || node.id}" is missing a ${missingPath} path`,
          fixable: true
        });
      }
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
  }, [nodes, edges]);

  // Run local validation when nodes or edges change
  useEffect(() => {
    if (isInitialized.current) {
      performLocalValidation();
    }
  }, [nodes, edges, performLocalValidation]);

  // ============================================
  // EDGE CONNECTION CONSTRAINTS
  // ============================================
  const isValidConnection = useCallback((connection) => {
    const { source, target, sourceHandle } = connection;

    // Prevent self-connections
    if (source === target) {
      return false;
    }

    // Find source and target nodes
    const sourceNode = nodes.find(n => n.id === source);
    const targetNode = nodes.find(n => n.id === target);

    if (!sourceNode || !targetNode) {
      return false;
    }

    // Prevent connecting TO a start node (it's source only)
    if (targetNode.type === 'start') {
      return false;
    }

    // Prevent connecting FROM an end node (it's target only)
    if (sourceNode.type === 'end') {
      return false;
    }

    // Check if connection already exists
    const connectionExists = edges.some(
      e => e.source === source && e.target === target && e.sourceHandle === sourceHandle
    );
    if (connectionExists) {
      return false;
    }

    return true;
  }, [nodes, edges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setConfigPanelOpen(true);
    // Clicking a node makes it the source the next dropped node chains from.
    chainSourceRef.current = node.id;
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const componentData = JSON.parse(event.dataTransfer.getData('application/json'));
      if (!componentData) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Map component IDs to node types. NOTE: agent_* nodes stay type 'step' so the
      // engine executes them (it only runs start/end/action/condition/parallel/step;
      // review/escalation types are skipped). They're identified by their "Agent:"
      // label + the "AI Agent" palette category.
      const nodeType = componentData.id === 'condition' ? 'condition' :
                       componentData.id === 'parallel' ? 'parallel' :
                       componentData.id === 'review-step' ? 'review' : 'step';

      const newId = `${componentData.id}-${Date.now()}`;
      const newNode = {
        id: newId,
        type: nodeType,
        position,
        data: {
          label: componentData.name,
          description: componentData.description,
          componentType: componentData.id,
          // Required by backend schema
          component_id: componentData.id,
          name: componentData.name,
        },
      };

      // Auto-connect the new node to the most recent node in the chain so users
      // don't have to drag an edge for every linear step. Picks, in order:
      //   1. the last node added/clicked (chainSourceRef)
      //   2. the node currently feeding into End (natural insertion tail)
      //   3. any connectable node with no outgoing edge
      //   4. the Start node
      // Skips condition/parallel/end as sources (their outputs need explicit handles).
      const byId = (id) => nodes.find((n) => n.id === id);
      const isConnectableSource = (n) =>
        !!n && n.type !== 'end' && n.type !== 'condition' && n.type !== 'parallel';

      let sourceId = null;
      if (isConnectableSource(byId(chainSourceRef.current))) {
        sourceId = chainSourceRef.current;
      } else {
        const edgeToEnd = edges.find((e) => e.target === 'end');
        if (edgeToEnd && isConnectableSource(byId(edgeToEnd.source))) {
          sourceId = edgeToEnd.source;
        } else {
          const tail = nodes.find(
            (n) => isConnectableSource(n) && !edges.some((e) => e.source === n.id)
          );
          sourceId = tail?.id || (byId('start') ? 'start' : null);
        }
      }

      setNodes((nds) => nds.concat(newNode));
      if (sourceId && sourceId !== newId) {
        setEdges((eds) =>
          eds.concat({
            id: `edge_${sourceId}_${newId}`,
            source: sourceId,
            target: newId,
            type: 'smoothstep',
            animated: false,
          })
        );
      }
      // The node just dropped becomes the source for the next drop.
      chainSourceRef.current = newId;
    },
    [screenToFlowPosition, setNodes, setEdges, nodes, edges]
  );

  const handleDragStart = (event, component) => {
    event.dataTransfer.setData('application/json', JSON.stringify(component));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleNodeUpdate = (updatedNode) => {
    setNodes((nds) =>
      nds.map((node) => (node.id === updatedNode.id ? updatedNode : node))
    );
    setSelectedNode(updatedNode);
  };

  const handleDeleteNode = useCallback((nodeId) => {
    // Don't allow deleting start or end nodes
    if (nodeId === 'start' || nodeId === 'end') {
      toast.warning('Cannot delete start or end nodes');
      return;
    }
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode(null);
    setConfigPanelOpen(false);
  }, [setNodes, setEdges, toast]);

  // Handle adding node from quick actions bar
  const handleAddNodeFromQuickActions = useCallback((nodeData) => {
    const id = `node_${Date.now()}`;
    const newNode = {
      id,
      type: nodeData.type || 'step',
      position: nodeData.position || { x: 250, y: 250 },
      data: {
        label: nodeData.data?.label || 'New Node',
        ...nodeData.data,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setQuickActionsVisible(false);

    // Auto-connect to selected node if one exists
    if (selectedNode) {
      const newEdge = {
        id: `edge_${selectedNode.id}_${id}`,
        source: selectedNode.id,
        target: id,
      };
      setEdges((eds) => [...eds, newEdge]);
    }
  }, [setNodes, setEdges, selectedNode]);

  // Fix condition warning by adding missing path
  const handleFixConditionWarning = useCallback((warning, resolution) => {
    if (warning.type !== 'condition_incomplete') return;

    const conditionNode = nodes.find(n => n.id === warning.nodeId);
    if (!conditionNode) return;

    const isYesPath = warning.missingPath === 'Yes';
    const handle = isYesPath ? 'yes' : 'no';

    // Position new node below and to the side of condition
    const offsetX = isYesPath ? 150 : -150;
    const newPosition = {
      x: conditionNode.position.x + offsetX,
      y: conditionNode.position.y + 150,
    };

    // Create new node based on resolution choice
    const nodeId = `${resolution}-${Date.now()}`;
    let newNode;

    if (resolution === 'approve') {
      newNode = {
        id: nodeId,
        type: 'end',
        position: newPosition,
        data: {
          label: 'Auto Approve',
          outcome: 'approved',
          component_id: 'auto_approve',
        },
      };
    } else if (resolution === 'review') {
      newNode = {
        id: nodeId,
        type: 'review',
        position: newPosition,
        data: {
          label: 'Manual Review',
          component_id: 'review-step',
          name: 'Manual Review',
        },
      };
    } else if (resolution === 'reject') {
      newNode = {
        id: nodeId,
        type: 'end',
        position: newPosition,
        data: {
          label: 'Auto Reject',
          outcome: 'rejected',
          component_id: 'auto_reject',
        },
      };
    }

    // Create edge from condition to new node
    const newEdge = {
      id: `edge_${warning.nodeId}_${nodeId}`,
      source: warning.nodeId,
      target: nodeId,
      sourceHandle: handle,
      label: warning.missingPath,
    };

    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [...eds, newEdge]);
    toast.success(`Added ${newNode.data.label} for ${warning.missingPath} path`);
  }, [nodes, setNodes, setEdges, toast]);

  // Handle duplicate node
  const handleDuplicateNode = useCallback((node) => {
    if (!node) return;
    const id = `node_${Date.now()}`;
    const newNode = {
      ...node,
      id,
      position: {
        x: node.position.x + 50,
        y: node.position.y + 50,
      },
      selected: false,
    };
    setNodes((nds) => [...nds, newNode]);
    toast.success('Node duplicated');
  }, [setNodes, toast]);

  // Toggle quick actions bar
  const handleToggleQuickActions = useCallback(() => {
    setQuickActionsVisible((v) => !v);
    setQuickActionsPosition(null); // Center position
  }, []);

  // Use keyboard shortcuts hook
  useWorkflowKeyboardShortcuts({
    onDeleteNode: (node) => handleDeleteNode(node.id),
    onDuplicateNode: handleDuplicateNode,
    onEditNode: (node) => {
      setSelectedNode(node);
      setConfigPanelOpen(true);
    },
    onUndo: handleUndo,
    onRedo: handleRedo,
    onToggleQuickActions: handleToggleQuickActions,
    selectedNode,
  });

  // Handle keyboard delete
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNode) {
        handleDeleteNode(selectedNode.id);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, handleDeleteNode]);

  // ============================================
  // CONTEXT MENU (Copy/Delete)
  // ============================================
  const handleNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      node,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleCopyNode = useCallback(() => {
    if (!contextMenu?.node) return;
    const node = contextMenu.node;

    // Don't allow copying start/end nodes
    if (node.type === 'start' || node.type === 'end') {
      toast.warning('Cannot copy start or end nodes');
      closeContextMenu();
      return;
    }

    const newNode = {
      id: `${node.data?.componentType || node.type}-${Date.now()}`,
      type: node.type,
      position: {
        x: node.position.x + 50,
        y: node.position.y + 50,
      },
      data: {
        ...node.data,
        label: `${node.data?.label || 'Node'} (Copy)`,
      },
    };

    setNodes(nds => [...nds, newNode]);
    closeContextMenu();
    toast.success('Node copied');
  }, [contextMenu, setNodes, closeContextMenu, toast]);

  const handleContextDelete = useCallback(() => {
    if (!contextMenu?.node) return;
    handleDeleteNode(contextMenu.node.id);
    closeContextMenu();
  }, [contextMenu, handleDeleteNode, closeContextMenu]);

  // Close context menu on click outside or escape
  useEffect(() => {
    const handleClickOutside = () => closeContextMenu();
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu, closeContextMenu]);

  const handleSave = async () => {
    const config = { nodes: transformNodesForBackend(nodes), edges };
    try {
      await updateWorkflow.mutateAsync({
        id: workflowId,
        data: { config },
      });
      // Update the saved config reference and clear unsaved changes indicator
      lastSavedConfigRef.current = JSON.stringify({ nodes: config.nodes, edges });
      setHasUnsavedChanges(false);
      toast.success('Workflow saved');
    } catch (error) {
      toast.error('Failed to save: ' + error.message);
    }
  };

  const handlePublish = async () => {
    // First validate
    let validation;
    try {
      validation = await validateWorkflow.mutateAsync({
        workflowId,
        config: { nodes: transformNodesForBackend(nodes), edges },
      });
    } catch (error) {
      toast.error('Validation failed: ' + (error.message || 'Unknown error'));
      return;
    }

    if (validation?.errors?.length > 0) {
      const proceed = window.confirm(
        `Workflow has ${validation.errors.length} errors:\n` +
        validation.errors.map((e) => `- ${e.message}`).join('\n') +
        '\n\nPublish anyway?'
      );
      if (!proceed) return;
    }

    try {
      await publishWorkflow.mutateAsync({ workflowId });
      toast.success('Workflow published successfully!');
    } catch (error) {
      toast.error('Failed to publish: ' + error.message);
    }
  };

  const handlePreview = async () => {
    // Save first if there are changes
    if (nodes.length > 0) {
      try {
        await handleSave();
      } catch (error) {
        // Continue anyway - preview will use last saved version
      }
    }

    try {
      const result = await previewWorkflow.mutateAsync({
        workflowId,
        useCurrentConfig: true,
      });

      // Open preview in new window
      window.open(result.preview_url, '_blank', 'width=500,height=700,scrollbars=yes');
    } catch (error) {
      toast.error('Failed to start preview: ' + (error.message || 'Please save the workflow first'));
    }
  };

  // Auto-arrange nodes using dagre layout
  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) {
      toast.warning('No nodes to arrange');
      return;
    }
    const layoutedNodes = autoLayoutNodes(nodes, edges, 'TB');
    setNodes(layoutedNodes);
    // Center the view on the arranged nodes
    setTimeout(() => centerWorkflowOnce(), 100);
    toast.success('Nodes arranged');
  }, [nodes, edges, setNodes, toast, centerWorkflowOnce]);

  if (isLoading) {
    return (
      <div className="canvas-loading">
        <Loader2 size={32} className="spin" />
        <span>Loading workflow...</span>
      </div>
    );
  }

  return (
    <div className="workflow-canvas-container" data-testid="workflow-canvas" data-workflow-id={workflowId}>
      <ComponentLibrary
        onDragStart={handleDragStart}
        collapsed={libraryCollapsed}
        onToggle={() => setLibraryCollapsed(!libraryCollapsed)}
        onBack={handleBack}
      />

      <div className="canvas-main" ref={reactFlowWrapper}>
        <div className="canvas-toolbar">
          <div className="toolbar-left">
            <span className="workflow-name" title={workflow?.name || 'Untitled'}>{workflow?.name || 'Untitled'}</span>
            <span className={`workflow-status ${workflow?.status || 'draft'}`}>
              {workflow?.status || 'draft'}
            </span>
            {/* Unsaved changes indicator */}
            {hasUnsavedChanges && (
              <span className="unsaved-indicator" title="Unsaved changes">
                <span className="unsaved-dot" />
                Unsaved
              </span>
            )}
          </div>
          <div className="toolbar-actions">
            {/* Undo/Redo/Layout - icon only, compact */}
            <button className="icon-only" onClick={handleUndo} disabled={!canUndo} title="Undo (⌘Z)" data-testid="undo-btn">
              <Undo2 size={16} />
            </button>
            <button className="icon-only" onClick={handleRedo} disabled={!canRedo} title="Redo (⌘⇧Z)" data-testid="redo-btn">
              <Redo2 size={16} />
            </button>
            <button className="icon-only" onClick={handleAutoLayout} title="Auto-arrange nodes" data-testid="auto-layout-btn">
              <LayoutGrid size={16} />
            </button>
            <div className="divider" />
            <button
              onClick={handleSave}
              disabled={updateWorkflow.isPending || !workflowId}
              className={hasUnsavedChanges ? 'has-changes' : ''}
              title={hasUnsavedChanges ? "Save workflow (unsaved changes)" : "Save workflow"}
              data-testid="save-btn"
            >
              {updateWorkflow.isPending ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Cloud size={16} />
              )}
              <span className="btn-label">Save{hasUnsavedChanges ? '*' : ''}</span>
            </button>
            <button
              onClick={handlePreview}
              disabled={previewWorkflow.isPending || !workflowId}
              title="Preview this workflow"
              data-testid="preview-btn"
            >
              {previewWorkflow.isPending ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Eye size={16} />
              )}
              <span className="btn-label">Preview</span>
            </button>
            <button className="primary" onClick={handlePublish} disabled={publishWorkflow.isPending} data-testid="publish-btn">
              {publishWorkflow.isPending ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Play size={16} />
              )}
              <span className="btn-label">Publish</span>
            </button>
          </div>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onNodeContextMenu={handleNodeContextMenu}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          isValidConnection={isValidConnection}
          defaultEdgeOptions={{ type: 'smoothstep', animated: false }}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
          snapToGrid
          snapGrid={[15, 15]}
          selectionOnDrag
          selectNodesOnDrag
          panOnDrag={[1, 2]}
          panOnScroll
          minZoom={0.1}
          maxZoom={2}
          selectionMode="partial"
        >
          <Controls />
          <MiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>

        {/* Quick Actions Bar */}
        <QuickActionsBar
          visible={quickActionsVisible}
          position={quickActionsPosition}
          onAddNode={handleAddNodeFromQuickActions}
          onClose={() => setQuickActionsVisible(false)}
          selectedNode={selectedNode}
        />

        {/* Live Validation Panel */}
        {(validationErrors.length > 0 || validationWarnings.length > 0) && (
          <div className="validation-panel" data-testid="validation-panel">
            {validationErrors.length > 0 && (
              <div className="validation-section errors">
                <div className="validation-header">
                  <AlertTriangle size={14} />
                  <span>{validationErrors.length} Error{validationErrors.length !== 1 ? 's' : ''}</span>
                </div>
                <ul>
                  {validationErrors.map((err, i) => (
                    <li key={i}>{err.message}</li>
                  ))}
                </ul>
              </div>
            )}
            {validationWarnings.length > 0 && (
              <div className="validation-section warnings">
                <div className="validation-header">
                  <AlertTriangle size={14} />
                  <span>{validationWarnings.length} Warning{validationWarnings.length !== 1 ? 's' : ''}</span>
                </div>
                <ul>
                  {validationWarnings.map((warn, i) => (
                    <li key={i} className={warn.fixable ? 'fixable-warning' : ''}>
                      <span>{warn.message}</span>
                      {warn.fixable && warn.type === 'condition_incomplete' && (
                        <div className="warning-fix-options">
                          <span className="fix-label">Quick fix:</span>
                          <button
                            className="fix-btn approve"
                            onClick={() => handleFixConditionWarning(warn, 'approve')}
                            title="Add Auto Approve node"
                          >
                            <CheckCircle size={12} />
                            Approve
                          </button>
                          <button
                            className="fix-btn review"
                            onClick={() => handleFixConditionWarning(warn, 'review')}
                            title="Add Manual Review node"
                          >
                            <Eye size={12} />
                            Review
                          </button>
                          <button
                            className="fix-btn reject"
                            onClick={() => handleFixConditionWarning(warn, 'reject')}
                            title="Add Auto Reject node"
                          >
                            <XCircle size={12} />
                            Reject
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Context Menu for Node Copy/Delete */}
        {contextMenu && (
          <div
            className="node-context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            data-testid="node-context-menu"
          >
            <button onClick={handleCopyNode} disabled={contextMenu.node?.type === 'start' || contextMenu.node?.type === 'end'}>
              <Copy size={14} />
              Copy
            </button>
            <button
              onClick={handleContextDelete}
              className="delete"
              disabled={contextMenu.node?.type === 'start' || contextMenu.node?.type === 'end'}
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}

{/* AIAssistantDock removed - using WorkflowChat at page level instead */}
      </div>

      <AnimatePresence>
        {configPanelOpen && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={handleNodeUpdate}
            onDelete={handleDeleteNode}
            onClose={() => {
              setConfigPanelOpen(false);
              setSelectedNode(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {executionPanelOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={panelSlideVariants}
          >
            <ExecutionMonitor
              workflowId={workflowId}
              onClose={() => setExecutionPanelOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unsaved Changes Modal */}
      <AnimatePresence>
        {showUnsavedModal && (
          <motion.div
            className="modal-overlay"
            onClick={() => setShowUnsavedModal(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal-content unsaved-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="unsaved-modal-icon">
                <AlertTriangle size={24} />
              </div>
              <h3>Unsaved Changes</h3>
              <p>You have unsaved changes that will be lost if you leave.</p>
              <div className="modal-actions">
                <motion.button
                  className="cancel-btn"
                  onClick={() => setShowUnsavedModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Keep Editing
                </motion.button>
                <motion.button
                  className="discard-btn"
                  onClick={handleDiscardChanges}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Discard Changes
                </motion.button>
                <motion.button
                  className="save-btn primary"
                  onClick={handleSaveAndClose}
                  disabled={updateWorkflow.isPending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {updateWorkflow.isPending ? <Loader2 size={14} className="spin" /> : <Cloud size={14} />}
                  Save & Exit
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export default function WorkflowBuilderPage() {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [initialChatPanel, setInitialChatPanel] = useState(null); // 'migration' to open with migration panel
  const [executionPanelOpen, setExecutionPanelOpen] = useState(false);
  const [pendingConfigUpdate, setPendingConfigUpdate] = useState(null);

  // Handle workflow created from chat
  const handleWorkflowCreated = useCallback((workflow) => {
    setSelectedWorkflowId(workflow.id);
    setChatPanelOpen(false);
  }, []);

  // Handle config updates from chat (when modifying existing workflow)
  const handleChatConfigUpdate = useCallback((config) => {
    if (!config) return;

    // Set pending config to be applied by WorkflowCanvas
    setPendingConfigUpdate(config);

    // Close the chat panel after applying
    setChatPanelOpen(false);
  }, []);

  // Clear pending config after it's been applied
  const handleConfigApplied = useCallback(() => {
    setPendingConfigUpdate(null);
  }, []);

  return (
    <div className="workflow-builder-page" data-testid="workflow-builder-page" data-ready={!selectedWorkflowId ? 'list' : 'canvas'}>
      <style>{`
        .workflow-builder-page {
          flex: 1;
          min-height: 0;
          height: 100%;
          display: flex;
          background: var(--bg-primary);
          overflow: hidden;
        }

        /* Workflow List */
        .workflow-list {
          width: 400px;
          min-width: 400px;
          height: 100%;
          border-right: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          background: var(--bg-secondary);
          overflow: hidden;
        }

        .workflow-list.loading {
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: var(--text-secondary);
        }

        .list-header {
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
        }

        .list-header h2 {
          font-size: 16px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .create-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .create-btn:hover {
          opacity: 0.9;
        }

        .create-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .list-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 12px;
          min-height: 0; /* Critical for flexbox scroll */
        }

        /* Templates Section - now uses list-content class */

        .template-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .template-item {
          padding: 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .template-item:hover {
          border-color: var(--accent-primary);
          background: var(--bg-hover);
        }

        .template-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 8px;
        }

        .template-name {
          font-weight: 600;
          font-size: 13px;
          color: var(--text-primary);
        }

        .template-desc {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .template-type {
          display: inline-block;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .template-type.onboarding {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .template-type.post_onboarding {
          background: var(--accent-glow-medium);
          color: var(--accent-primary);
        }

        .template-type.monitoring {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
        }

        .template-type.kyb {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: var(--text-secondary);
        }

        .empty-state h3 {
          margin: 16px 0 8px;
          font-size: 16px;
          color: var(--text-primary);
        }

        .empty-state p {
          font-size: 13px;
          margin-bottom: 16px;
        }

        .empty-state button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .workflow-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* Workflow Sidebar (when editing) */
        .workflow-sidebar {
          width: 60px;
          background: var(--bg-secondary);
          border-right: 1px solid var(--border-color);
          padding: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .back-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 12px 8px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 10px;
          text-align: center;
          transition: all 0.15s;
        }

        .back-button:hover {
          background: var(--bg-hover);
          border-color: var(--accent-primary);
          color: var(--text-primary);
        }

        .workflow-item {
          padding: 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.15s;
        }

        .workflow-item:hover {
          border-color: var(--accent-primary);
          background: var(--bg-hover);
        }

        .workflow-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .workflow-name {
          font-weight: 500;
          font-size: 14px;
          color: var(--text-primary);
        }

        .workflow-type {
          font-size: 12px;
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .workflow-meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .status {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }

        .status.draft {
          background: var(--bg-hover);
          color: var(--text-secondary);
        }

        .status.published {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .status.archived {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        /* Matched-pair row action buttons (Get link / Unpublish) */
        .row-action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          height: 26px;
          padding: 0 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          line-height: 1;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.15s;
        }

        .row-action-btn:disabled {
          cursor: wait;
          opacity: 0.7;
        }

        .get-link-btn {
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.35);
          color: #10b981;
        }

        .get-link-btn:hover:not(:disabled) {
          background: rgba(16, 185, 129, 0.2);
          border-color: rgba(16, 185, 129, 0.6);
        }

        .unpublish-btn {
          background: transparent;
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
        }

        .unpublish-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .delete-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 26px;
          width: 26px;
          padding: 0;
          background: transparent;
          border: 1px solid transparent;
          color: var(--text-muted);
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 24px;
          min-width: 360px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .modal-content h3 {
          margin: 0 0 16px;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .modal-content input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          background: var(--bg-tertiary);
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
          margin-bottom: 16px;
        }

        .modal-content input:focus {
          border-color: var(--accent-primary);
        }

        .modal-content input::placeholder {
          color: var(--text-muted);
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .modal-actions .cancel-btn {
          padding: 10px 20px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .modal-actions .cancel-btn:hover {
          background: var(--bg-hover);
        }

        .modal-actions .create-btn {
          padding: 10px 20px;
          background: var(--accent-primary);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .modal-actions .create-btn:hover {
          opacity: 0.9;
        }

        .modal-actions .create-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Delete Modal */
        .delete-modal {
          text-align: center;
        }

        .delete-modal-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .delete-modal h3 {
          margin-bottom: 8px;
        }

        .delete-modal p {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0 0 8px;
        }

        .delete-modal .delete-warning {
          color: #ef4444;
          font-size: 13px;
          margin-bottom: 20px;
        }

        .delete-modal .modal-actions {
          justify-content: center;
        }

        .modal-actions .delete-confirm-btn {
          padding: 10px 20px;
          background: #ef4444;
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .modal-actions .delete-confirm-btn:hover {
          background: #dc2626;
        }

        .modal-actions .delete-confirm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Unpublish Modal */
        .unpublish-modal p {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0 0 8px;
        }

        .unpublish-modal .unpublish-info {
          color: var(--text-muted);
          font-size: 13px;
          margin-bottom: 20px;
        }

        .modal-actions .unpublish-confirm-btn {
          padding: 10px 20px;
          background: #f59e0b;
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .modal-actions .unpublish-confirm-btn:hover {
          background: #d97706;
        }

        .modal-actions .unpublish-confirm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Unsaved Changes Modal */
        .unsaved-modal {
          max-width: 400px;
        }

        .unsaved-modal-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: rgba(251, 191, 36, 0.15);
          border-radius: 50%;
          margin: 0 auto 16px;
          color: #fbbf24;
        }

        .unsaved-modal h3 {
          text-align: center;
          margin-bottom: 8px;
        }

        .unsaved-modal p {
          text-align: center;
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 24px;
        }

        .unsaved-modal .modal-actions {
          display: flex;
          gap: 8px;
          justify-content: center;
        }

        .modal-actions .discard-btn {
          padding: 10px 16px;
          background: transparent;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .modal-actions .discard-btn:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        .modal-actions .save-btn {
          padding: 10px 16px;
          background: var(--accent-primary);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .modal-actions .save-btn:hover {
          opacity: 0.9;
        }

        .modal-actions .save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Canvas Container */
        .workflow-canvas-container {
          flex: 1;
          display: flex;
          overflow: hidden;
          position: relative;
        }

        .canvas-loading {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: var(--text-secondary);
        }

        /* Component Library */
        .component-library {
          width: 280px;
          background: var(--bg-secondary);
          border-right: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          transition: width 0.2s;
        }

        .component-library.collapsed {
          width: 48px;
          align-items: center;
          padding-top: 12px;
        }

        .back-to-workflows-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: transparent;
          border: none;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
          width: 100%;
          transition: background 0.15s, color 0.15s;
        }

        .back-to-workflows-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .library-header {
          padding: 16px;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .library-header h3 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .toggle-btn {
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: 4px;
        }

        .toggle-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .search-box {
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary);
          margin: 12px;
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .search-box input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-size: 13px;
          color: var(--text-primary);
        }

        .search-box input::placeholder {
          color: var(--text-muted);
        }

        .search-box button {
          padding: 2px;
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
        }

        .categories {
          flex: 1;
          overflow-y: auto;
          padding: 0 12px 12px;
        }

        .category {
          margin-bottom: 8px;
        }

        .category-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: transparent;
          border: none;
          color: var(--text-primary);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 4px;
          letter-spacing: 0.5px;
        }

        .category-header:hover {
          background: var(--bg-hover);
          color: var(--accent-primary);
        }

        .components {
          padding: 4px 0;
        }

        .component-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 6px;
          cursor: grab;
          transition: all 0.15s;
          border: 1px solid transparent;
          color: var(--text-primary);
        }

        .component-item:hover {
          background: var(--bg-hover);
          border-color: var(--border-color);
        }

        .component-item:active {
          cursor: grabbing;
        }

        .component-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .component-name {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .component-desc {
          font-size: 11px;
          color: var(--text-secondary);
        }

        /* Canvas Main */
        .canvas-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .canvas-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toolbar-left .workflow-name {
          font-weight: 600;
          font-size: 14px;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-primary);
        }

        .toolbar-left .workflow-status {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .saving-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        /* Unsaved changes indicator */
        .unsaved-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: rgba(251, 191, 36, 0.15);
          border: 1px solid rgba(251, 191, 36, 0.3);
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          color: #fbbf24;
        }

        .unsaved-dot {
          width: 6px;
          height: 6px;
          background: #fbbf24;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Save button with unsaved changes */
        .toolbar-actions button.has-changes {
          background: rgba(251, 191, 36, 0.15);
          border-color: rgba(251, 191, 36, 0.4);
          color: #fbbf24;
        }

        .toolbar-actions button.has-changes:hover {
          background: rgba(251, 191, 36, 0.25);
          border-color: rgba(251, 191, 36, 0.6);
        }

        /* Toolbar button labels */
        .toolbar-actions .btn-label {
          display: inline;
        }

        @media (max-width: 900px) {
          .toolbar-actions .btn-label {
            display: none;
          }
        }

        .toolbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .toolbar-actions button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-primary);
          font-size: 13px;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .toolbar-actions button:hover {
          background: var(--bg-hover);
          border-color: var(--accent-primary);
        }

        .toolbar-actions button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toolbar-actions button.icon-only {
          padding: 6px 8px;
          min-width: 32px;
        }

        .toolbar-actions button.primary {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: white;
        }

        .toolbar-actions button.primary:hover {
          opacity: 0.9;
        }

        .toolbar-actions .divider {
          width: 1px;
          height: 24px;
          background: var(--border-color);
        }

        /* React Flow overrides */
        .react-flow {
          flex: 1;
          background: var(--bg-primary);
        }

        .react-flow__node {
          font-family: 'DM Sans', sans-serif;
        }

        .react-flow__edge-path {
          stroke: var(--text-muted);
          stroke-width: 1.5;
        }

        .react-flow__edge.selected .react-flow__edge-path {
          stroke: var(--accent-primary);
          stroke-width: 2;
        }

        .react-flow__edge:hover .react-flow__edge-path {
          stroke: var(--text-secondary);
        }

        .react-flow__controls {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          overflow: hidden;
          bottom: 60px !important;
          left: 20px !important;
        }

        .react-flow__controls-button {
          background: var(--bg-secondary);
          border: none;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-secondary);
        }

        .react-flow__controls-button:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .react-flow__minimap {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          bottom: 60px !important;
          right: 20px !important;
        }

        /* AI Assistant Dock */
        .ai-dock-toggle {
          position: absolute;
          bottom: 20px;
          right: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
          color: white;
          border: none;
          border-radius: 24px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          box-shadow: 0 4px 12px var(--accent-border);
          z-index: 10;
        }

        .ai-dock-toggle:hover {
          opacity: 0.9;
        }

        .ai-assistant-dock {
          position: absolute;
          bottom: 20px;
          right: 20px;
          width: 400px;
          max-height: 500px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          z-index: 10;
        }

        .dock-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);
        }

        .dock-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 14px;
        }

        .dock-header button {
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: 4px;
        }

        .dock-header button:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .dock-suggestions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);
          overflow-x: auto;
        }

        .suggestions-label {
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
        }

        .suggestion-chip {
          padding: 6px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          font-size: 12px;
          color: var(--text-secondary);
          cursor: pointer;
          white-space: nowrap;
        }

        .suggestion-chip:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .dock-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          min-height: 200px;
        }

        .dock-messages .empty-state {
          padding: 20px;
        }

        .dock-messages .empty-state p {
          margin: 8px 0 0;
          font-size: 14px;
        }

        .dock-messages .empty-state .hint {
          font-size: 12px;
          color: var(--text-muted);
        }

        .message {
          margin-bottom: 16px;
        }

        .message.user .message-content {
          background: var(--accent-primary);
          color: white;
          padding: 10px 14px;
          border-radius: 12px 12px 4px 12px;
          margin-left: 40px;
        }

        .message.assistant .message-content {
          background: var(--bg-tertiary);
          padding: 10px 14px;
          border-radius: 12px 12px 12px 4px;
          margin-right: 40px;
        }

        .message.error .message-content {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          padding: 10px 14px;
          border-radius: 8px;
        }

        .message.system .message-content {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 10px 14px;
          border-radius: 8px;
        }

        .suggestion-actions {
          margin-top: 12px;
        }

        .apply-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .apply-btn:hover {
          opacity: 0.9;
        }

        .apply-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .dock-input {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid var(--border-color);
        }

        .dock-input input {
          flex: 1;
          padding: 10px 14px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 13px;
          color: var(--text-primary);
          outline: none;
        }

        .dock-input input:focus {
          border-color: var(--accent-primary);
        }

        .dock-input input::placeholder {
          color: var(--text-muted);
        }

        .dock-input button {
          padding: 10px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .dock-input button:hover {
          opacity: 0.9;
        }

        .dock-input button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Config Panel - always overlay to maximize canvas space */
        .config-panel {
          position: absolute;
          right: 0;
          top: 0;
          width: 320px;
          height: 100%;
          background: var(--bg-secondary);
          border-left: 1px solid var(--border-color);
          z-index: 50;
          box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
        }

        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid var(--border-color);
        }

        .panel-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .panel-header button {
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: 4px;
        }

        .panel-header button:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .panel-content {
          padding: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }

        .form-group input[type="text"],
        .form-group input[type="number"],
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 13px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          border-color: var(--accent-primary);
        }

        .form-group .checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .form-group .checkbox input {
          width: 16px;
          height: 16px;
        }

        .form-group .checkbox span {
          font-size: 13px;
          color: var(--text-primary);
        }

        .delete-section {
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
        }

        .delete-node-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          color: #ef4444;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .delete-node-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: #ef4444;
        }

        .delete-hint {
          display: block;
          margin-top: 8px;
          font-size: 11px;
          color: var(--text-muted);
          text-align: center;
        }

        /* Utilities */
        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Validation Panel - positioned at top for visibility */
        .validation-panel {
          position: absolute;
          top: 70px;
          left: 20px;
          max-width: 360px;
          max-height: 250px;
          overflow-y: auto;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 10;
          font-size: 12px;
        }

        .validation-section {
          padding: 10px 12px;
        }

        .validation-section.errors {
          background: rgba(239, 68, 68, 0.05);
          border-bottom: 1px solid var(--border-color);
        }

        .validation-section.warnings {
          background: rgba(245, 158, 11, 0.05);
        }

        .validation-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .validation-section.errors .validation-header {
          color: #ef4444;
        }

        .validation-section.warnings .validation-header {
          color: #f59e0b;
        }

        .validation-section ul {
          margin: 0;
          padding-left: 20px;
          color: var(--text-secondary);
        }

        .validation-section ul li {
          margin: 2px 0;
          line-height: 1.4;
        }

        .validation-section ul li.fixable-warning {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
        }

        .validation-section ul li.fixable-warning:last-child {
          border-bottom: none;
        }

        .warning-fix-options {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: -4px;
          flex-wrap: wrap;
        }

        .warning-fix-options .fix-label {
          font-size: 10px;
          color: var(--text-muted, #666);
          margin-right: 2px;
        }

        .warning-fix-options .fix-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          font-size: 11px;
          font-weight: 500;
          border-radius: 4px;
          border: 1px solid var(--border-color);
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .warning-fix-options .fix-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .warning-fix-options .fix-btn.approve:hover {
          background: rgba(34, 197, 94, 0.15);
          border-color: rgba(34, 197, 94, 0.4);
          color: #22c55e;
        }

        .warning-fix-options .fix-btn.review:hover {
          background: rgba(245, 158, 11, 0.15);
          border-color: rgba(245, 158, 11, 0.4);
          color: #f59e0b;
        }

        .warning-fix-options .fix-btn.reject:hover {
          background: rgba(239, 68, 68, 0.15);
          border-color: rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }

        /* Node Context Menu */
        .node-context-menu {
          position: fixed;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          min-width: 140px;
          padding: 4px;
        }

        .node-context-menu button {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          color: var(--text-primary);
          cursor: pointer;
          text-align: left;
        }

        .node-context-menu button:hover {
          background: var(--bg-hover);
        }

        .node-context-menu button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .node-context-menu button.delete {
          color: #ef4444;
        }

        .node-context-menu button.delete:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        /* Chat FAB */
        .chat-fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 56px;
          height: 56px;
          border-radius: 28px;
          background: linear-gradient(135deg, var(--accent-primary), var(--accent-primary));
          border: none;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 4px var(--accent-glow-medium);
          transition: all 0.2s;
          z-index: 1000;
        }

        .chat-fab:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4), 0 0 0 6px var(--accent-glow);
        }

        .chat-fab.active {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        /* Chat Panel Overlay - Animation handled by Framer Motion */
        .chat-panel-overlay {
          position: fixed;
          top: 0;
          right: 0;
          width: 520px;
          height: 100vh;
          z-index: 999;
          display: flex;
        }

        /* Responsive: Medium screens - collapse library by default */
        @media (max-width: 1200px) {
          .component-library {
            width: 48px;
          }
          .component-library:not(.collapsed) {
            width: 240px;
          }
          .toolbar-actions button:not(.primary) span {
            display: none;
          }
        }

        /* Responsive: Small screens - mobile-friendly panels */
        @media (max-width: 768px) {
          .component-library {
            position: absolute;
            z-index: 100;
            height: calc(100vh - 56px);
          }
          .component-library.collapsed {
            width: 0;
            overflow: hidden;
            padding: 0;
          }
          .config-panel {
            width: 100%;
            max-width: 320px;
          }
          .chat-panel-overlay {
            width: 100%;
          }
          .chat-fab {
            bottom: 16px;
            right: 16px;
          }
        }
      `}</style>

      {!selectedWorkflowId ? (
        <WorkflowList
          onSelect={setSelectedWorkflowId}
          onImportClick={() => {
            setInitialChatPanel('migration');
            setChatPanelOpen(true);
          }}
        />
      ) : (
        <ReactFlowProvider>
          <WorkflowCanvas
            workflowId={selectedWorkflowId}
            pendingConfigUpdate={pendingConfigUpdate}
            onConfigApplied={handleConfigApplied}
            executionPanelOpen={executionPanelOpen}
            setExecutionPanelOpen={setExecutionPanelOpen}
            onBack={() => setSelectedWorkflowId(null)}
          />
        </ReactFlowProvider>
      )}

      {/* Chat FAB - Always visible */}
      <motion.button
        className={`chat-fab ${chatPanelOpen ? 'active' : ''}`}
        onClick={() => setChatPanelOpen(!chatPanelOpen)}
        title="Build workflows with AI"
        data-testid="chat-fab"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.3 }}
      >
        <motion.div
          initial={false}
          animate={{ rotate: chatPanelOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {chatPanelOpen ? <X size={24} /> : <MessageSquare size={24} />}
        </motion.div>
      </motion.button>

      {/* Chat Panel - AnimatePresence for smooth transitions */}
      <AnimatePresence>
        {chatPanelOpen && (
          <motion.div
            className="chat-panel-overlay"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            data-testid="chat-panel"
            data-open={chatPanelOpen}
          >
            <WorkflowChat
              workflowId={selectedWorkflowId}
              onWorkflowCreated={handleWorkflowCreated}
              onConfigUpdate={handleChatConfigUpdate}
              onClose={() => {
                setChatPanelOpen(false);
                setInitialChatPanel(null); // Reset on close
              }}
              initialPanel={initialChatPanel}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
